import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  addTicketGiftCard,
  addTicketProducts,
  addTicketServices,
  deleteTicket,
  deleteTicketItem,
  downloadTicketReceiptPdf,
  emailTicketReceipt,
  getRefundContext,
  getTicketServiceStaffAvailability,
  getTicketById,
  listTicketRefunds,
  listTicketPayments,
  reopenTicket,
  setTicketTaxExempt,
  updateTicketItem,
  updateTicket,
} from "../api/tickets";
import { getTicketReview, resendTicketClientReview } from "../api/clientReviews";
import {

  deleteAppointment,

  getAppointment,

  getAppointmentLogs,

  listAppointments,

  updateAppointment,

} from "../api/appointments";

import { listServices } from "../api/services";
import { getClientById, updateClient } from "../api/clients";

import {

  APPOINTMENT_TYPES,

  appointmentTypeLabel,

  normalizeAppointmentType,

} from "../constants/appointmentTypes";

import NewClientModal from "../components/clients/NewClientModal";

import SBINModal from "../components/modals/SBINModal";

import SBINPageCard from "../components/layout/SBINPageCard";

import PreBookNextVisitModal from "../components/modals/PreBookNextVisitModal";

import AppointmentLogModal from "../components/modals/AppointmentLogModal";
import DeleteTicketModal from "../components/modals/DeleteTicketModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import ReopenTicketModal from "../components/modals/ReopenTicketModal";
import RefundTicketModal from "../components/modals/RefundTicketModal";
import ModalCloseX from "../components/common/ModalCloseX";
import ServiceSearch from "../components/services/ServiceSearch";
import { getUserPermissions } from "../api/salonPermissions";
import { useAuth } from "../context/AuthContext";
import {
  formatDateTimeInBusinessTZ,
  formatDateTimeInTimeZone,
  formatInTimeZone,
} from "../utils/datetime";
import { formatDateOnlyYYYYMMDD } from "../utils/dateOnly";
import { getSalonTimezone } from "../utils/salonTimezone";
import { reorderServicesSelectedFirst } from "../utils/serviceOrdering";
import { normalizeForSearch } from "../utils/normalizeSearch";
import "./TicketDetail.css";

const APPOINTMENTS_VERSION_STORAGE_KEY = "sbin:appointmentsVersion";

function formatDateTime(value, timeZone) {
  return formatDateTimeInBusinessTZ(value, timeZone);
}

function formatDateTimeRange(start, end, timeZone) {
  if (!start) return "--";
  const startLabel = formatDateTimeInTimeZone(start, timeZone);
  if (!end) return startLabel;
  const endLabel = formatInTimeZone(end, timeZone, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${startLabel} - ${endLabel}`;
}


function formatMoney(value) {

  const num = Number(value || 0);

  if (Number.isNaN(num)) return "$0.00";

  return `$${num.toFixed(2)}`;

}



function getLineSubtotal(item) {

  if (!item) return 0;

  if (item.line_subtotal != null) {

    const value = Number(item.line_subtotal);

    return Number.isFinite(value) ? value : 0;

  }

  const qty = Number(item.quantity || 0);

  const unit = Number(item.unit_price || 0);

  const discount = Number(item.line_discount || 0);

  const subtotal = qty * unit - discount;

  return Number.isFinite(subtotal) ? subtotal : 0;

}



function getItemAmount(item) {

  if (!item) return 0;

  const value =

    item.line_total ??

    item.line_subtotal ??

    item.line_total_incl_tax ??

    item.unit_price ??

    0;

  const num = Number(value);

  return Number.isFinite(num) ? num : 0;

}



function getTicketTypeLabel(ticket) {

  if (!ticket) return "Sales";

  if (ticket.is_refund === true) return "Refund";

  const rawType =

    ticket.ticket_type ||

    ticket.type ||

    ticket.kind ||

    ticket.ticketType ||

    ticket.ticketKind;

  const normalized = (rawType || "").toString().toLowerCase();

  if (normalized === "payment_in") return "Payment In";

  if (normalized === "payment_out") return "Payment Out";

  if (normalized === "refund") return "Refund";

  if (["sale", "sales"].includes(normalized)) return "Sales";

  if (typeof ticket.grand_total === "number" && ticket.grand_total < 0) {

    return "Refund";

  }

  return "Sales";

}



function formatRatePercent(value) {
  if (value == null) return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return `${num.toFixed(4).replace(/\.?0+$/, "")}%`;
}

function formatSendStatus(value) {
  const raw = (value || "").toString().toLowerCase();
  if (raw === "sent") return "Sent";
  if (raw === "failed") return "Failed";
  if (raw === "skipped") return "Skipped";
  return "Pending";
}

function StarIcon({ filled }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={`h-4 w-4 ${filled ? "text-amber-400" : "text-slate-300"}`}
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.955a1 1 0 0 0 .95.69h4.158c.969 0 1.371 1.24.588 1.81l-3.363 2.444a1 1 0 0 0-.364 1.118l1.287 3.955c.3.921-.755 1.688-1.54 1.118l-3.364-2.444a1 1 0 0 0-1.175 0l-3.364 2.444c-.784.57-1.838-.197-1.539-1.118l1.287-3.955a1 1 0 0 0-.364-1.118L2.05 9.382c-.783-.57-.38-1.81.588-1.81h4.159a1 1 0 0 0 .95-.69l1.302-3.955Z" />
    </svg>
  );
}

function renderStarRow(value) {
  const rating = Number(value);
  if (!Number.isFinite(rating) || rating <= 0) {
    return <span className="text-xs text-slate-400">--</span>;
  }
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, idx) => (
        <StarIcon key={idx} filled={idx < rating} />
      ))}
      <span className="text-xs text-slate-500">{rating}/5</span>
    </div>
  );
}

function formatTimeInput(value, timeZone) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      });
      const parts = formatter.formatToParts(date);
      const lookup = { hour: "", minute: "" };
      for (const part of parts) {
        if (Object.prototype.hasOwnProperty.call(lookup, part.type)) {
          lookup[part.type] = part.value;
        }
      }
      if (lookup.hour && lookup.minute) {
        return `${lookup.hour}:${lookup.minute}`;
      }
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}


export default function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { currentSalon, token, user } = useAuth();


  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ticketReview, setTicketReview] = useState(null);
  const [ticketReviewLoading, setTicketReviewLoading] = useState(false);
  const [ticketReviewError, setTicketReviewError] = useState("");
  const [ticketReviewResendLoading, setTicketReviewResendLoading] = useState(false);
  const [ticketReviewResendError, setTicketReviewResendError] = useState("");
  const [ticketReviewResendNotice, setTicketReviewResendNotice] = useState("");


  const [isDirty, setIsDirty] = useState(false);

  const [notes, setNotes] = useState("");

  const [servicesModalOpen, setServicesModalOpen] = useState(false);

  const [giftCardModalOpen, setGiftCardModalOpen] = useState(false);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [serviceCatalogLoading, setServiceCatalogLoading] = useState(false);
  const [serviceCatalogError, setServiceCatalogError] = useState("");
  const [serviceQuery, setServiceQuery] = useState("");
  const [serviceSelection, setServiceSelection] = useState({});
  const [eligibleStaffByServiceId, setEligibleStaffByServiceId] = useState({});
  const [serviceStaffAvailabilityLoading, setServiceStaffAvailabilityLoading] =
    useState(false);
  const originalServiceIndexMapRef = useRef(null);
  const [productsModalOpen, setProductsModalOpen] = useState(false);
  const [productCatalog, setProductCatalog] = useState([]);
  const [productCatalogLoading, setProductCatalogLoading] = useState(false);
  const [productCatalogError, setProductCatalogError] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [productSelection, setProductSelection] = useState({});
  const originalProductIndexMapRef = useRef(null);
  const [giftCardCode, setGiftCardCode] = useState("");
  const [giftCardAmount, setGiftCardAmount] = useState("");
  const [giftCardModalError, setGiftCardModalError] = useState("");
  const [banner, setBanner] = useState("");

  const [unitPriceDrafts, setUnitPriceDrafts] = useState({});
  const [unitPriceSaving, setUnitPriceSaving] = useState({});
  const [staffAssignSaving, setStaffAssignSaving] = useState({});
  const [discountModalOpen, setDiscountModalOpen] = useState(false);

  const [discountItem, setDiscountItem] = useState(null);

  const [discountType, setDiscountType] = useState("percent");

  const [discountValue, setDiscountValue] = useState("");

  const [discountSaving, setDiscountSaving] = useState(false);

  const [taxConfirmOpen, setTaxConfirmOpen] = useState(false);

  const [taxMessageOpen, setTaxMessageOpen] = useState(false);

  const [taxSubmitting, setTaxSubmitting] = useState(false);

  const [isClientModalOpen, setIsClientModalOpen] = useState(false);

  const [clientToEdit, setClientToEdit] = useState(null);

  const [clientModalError, setClientModalError] = useState("");

  const [appointmentForTicket, setAppointmentForTicket] = useState(null);

  const [apptForTypeLoading, setApptForTypeLoading] = useState(false);

  const [isApptTypeModalOpen, setIsApptTypeModalOpen] = useState(false);

  const [apptTypeDraft, setApptTypeDraft] = useState("");

  const [savingApptType, setSavingApptType] = useState(false);

  const [apptTypeError, setApptTypeError] = useState("");

  const [futureAppts, setFutureAppts] = useState([]);

  const [futureApptsError, setFutureApptsError] = useState("");

  const [futureApptsLoading, setFutureApptsLoading] = useState(false);

  const [isFutureModalOpen, setIsFutureModalOpen] = useState(false);

  const [deletingFutureApptId, setDeletingFutureApptId] = useState(null);

  const [isServiceLogOpen, setIsServiceLogOpen] = useState(false);

  const [isApptLogOpen, setIsApptLogOpen] = useState(false);

  const [apptLogs, setApptLogs] = useState([]);

  const [apptLogsLoading, setApptLogsLoading] = useState(false);

  const [apptLogsError, setApptLogsError] = useState("");

  const [isPrebookOpen, setIsPrebookOpen] = useState(false);

  const [serviceLogClient, setServiceLogClient] = useState(null);

  const [permissionsBundle, setPermissionsBundle] = useState(null);

  const [permissionsLoading, setPermissionsLoading] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [reopenSubmitting, setReopenSubmitting] = useState(false);
  const [reopenIntent, setReopenIntent] = useState(null);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundContext, setRefundContext] = useState(null);
  const [, setRefundContextLoading] = useState(false);
  const [, setRefundContextError] = useState("");
  const [refunds, setRefunds] = useState([]);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [refundsError, setRefundsError] = useState("");
  const [refundReceiptId, setRefundReceiptId] = useState(null);
  const [receiptClientEmail, setReceiptClientEmail] = useState("");
  const [ticketPdfLoading, setTicketPdfLoading] = useState(false);
  const [ticketEmailSending, setTicketEmailSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);


  const role = (
    user?.role ||
    localStorage.getItem("role") ||
    ""
  ).toLowerCase();
  const isOwnerRole = role === "owner";
  const canEditClients = ["admin", "host", "owner"].includes(role);
  const canResendReview = ["admin", "host", "owner", "manager"].includes(role);
  const canDeleteTicket = Boolean(

    isOwnerRole || permissionsBundle?.effective?.["tickets.delete"]
  );

  const canReopenTicket = Boolean(
    isOwnerRole || permissionsBundle?.effective?.["tickets.reopen"]
  );
  const canRefundTicket = Boolean(
    isOwnerRole || permissionsBundle?.effective?.["tickets.refund"]
  );
  const normalizedTicketType = useMemo(() => {
    const rawType =
      ticket?.ticket_type ||
      ticket?.type ||
      ticket?.kind ||
      ticket?.ticketType ||
      ticket?.ticketKind;
    return (rawType || "").toString().toLowerCase();
  }, [ticket]);
  const isRefundTicket = useMemo(
    () => normalizedTicketType.startsWith("refund"),
    [normalizedTicketType]
  );
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );
  const ticketTimezone = ticket?.salon_timezone || salonTimezone;


  const resolveAppointmentIdFromTicket = (t) => {

    if (!t) return null;



    const direct =

      t.appointment_id ??

      t.appointment?.id ??

      t.appointmentId ??

      null;

    if (direct) return direct;



    const fromList = t.appointments?.[0]?.id ?? null;

    if (fromList) return fromList;



    const fromServices =

      t.services?.find((s) => s.appointment_id)?.appointment_id ??

      t.ticket_services?.find((s) => s.appointment_id)?.appointment_id ??

      t.items?.find((i) => i.appointment_id)?.appointment_id ??

      null;



    return fromServices ?? null;

  };



  const clientId = ticket?.client?.id ?? ticket?.client_id ?? null;
  const appointmentId = resolveAppointmentIdFromTicket(ticket);
  const emitScheduleRevenueRefresh = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        APPOINTMENTS_VERSION_STORAGE_KEY,
        String(Date.now())
      );
    } catch (_) {
      // Ignore localStorage write failures and still emit the event.
    }
    window.dispatchEvent(new CustomEvent("sbin:appointmentsChanged"));
  }, []);


  useEffect(() => {
    if (!token) {
      navigate("/login?next=/tickets");
    }
  }, [token, navigate]);

  useEffect(() => {
    let isActive = true;
    const ticketEmail = (ticket?.client?.email || "").trim();

    if (!clientId) {
      setReceiptClientEmail(ticketEmail);
      return () => {
        isActive = false;
      };
    }

    const loadClientEmail = async () => {
      try {
        const client = await getClientById(clientId);
        if (!isActive) return;
        const email = (client?.email || "").trim();
        setReceiptClientEmail(email || ticketEmail);
      } catch (_) {
        if (!isActive) return;
        setReceiptClientEmail(ticketEmail);
      }
    };

    loadClientEmail();
    return () => {
      isActive = false;
    };
  }, [clientId, ticket?.client?.email]);

  const loadTicketReview = useCallback(async () => {
    if (!ticketId) return;
    setTicketReviewLoading(true);
    setTicketReviewError("");
    try {
      const data = await getTicketReview(ticketId);
      setTicketReview(data || null);
    } catch (err) {
      const statusCode = err?.response?.status;
      if (statusCode === 404) {
        setTicketReview(null);
      } else {
        setTicketReviewError("Unable to load client feedback.");
      }
    } finally {
      setTicketReviewLoading(false);
    }
  }, [ticketId]);

  const handleResendReview = async () => {
    if (ticketReviewResendLoading) return;
    setTicketReviewResendError("");
    setTicketReviewResendNotice("");
    setTicketReviewResendLoading(true);
    try {
      const data = await resendTicketClientReview(ticketId);
      const nextReview = data || ticketReview;
      setTicketReview(nextReview);
      const nextStatus = (nextReview?.send_status || "").toString().toLowerCase();
      if (nextStatus === "sent") {
        setTicketReviewResendNotice("Review request has been sent.");
      } else if (nextStatus === "pending") {
        setTicketReviewResendNotice("Review request has been scheduled to send.");
      } else if (nextStatus === "failed") {
        setTicketReviewResendError(
          "Unable to send review request. Please check the client's email and email settings."
        );
      } else {
        setTicketReviewResendNotice("Review request has been updated.");
      }
    } catch (err) {
      setTicketReviewResendError(
        err?.response?.data?.detail || "Unable to resend review request."
      );
    } finally {
      setTicketReviewResendLoading(false);
    }
  };

  const fetchTicket = async () => {
    if (!ticketId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getTicketById(ticketId);
      setTicket(data);
      setNotes(data?.notes || "");
      setBanner("");
      setRefundReceiptId(null);
      loadTicketReview();
    } catch (err) {
      console.error(err);
      const statusCode = err?.response?.status;
      if (statusCode === 404) {

        setError(

          "This ticket no longer exists (it may have been removed because the appointment was deleted)."

        );

      } else {

        setError(err?.message || "Unable to load ticket");

      }

    } finally {
      setLoading(false);
    }
  };

  const showBanner = (message, receiptId = null) => {
    setBanner(message);
    setRefundReceiptId(receiptId);
  };

  const ticketReviewLastRequestLabel = useMemo(() => {
    if (!ticketReview?.review_request_id) return "Never sent";
    if (ticketReview.sent_at) {
      return formatDateTime(ticketReview.sent_at, ticketTimezone);
    }
    if (ticketReview.scheduled_send_at) {
      return `Pending email - ${formatDateTime(
        ticketReview.scheduled_send_at,
        ticketTimezone
      )}`;
    }
    return "Pending email";
  }, [
    ticketReview?.review_request_id,
    ticketReview?.scheduled_send_at,
    ticketReview?.sent_at,
    ticketTimezone,
  ]);

  const ticketReviewCompletedLabel = useMemo(() => {
    if (!ticketReview?.submitted_at) return "Not completed";
    return `Completed ${formatDateTime(ticketReview.submitted_at, ticketTimezone)}`;
  }, [ticketReview?.submitted_at, ticketTimezone]);


  useEffect(() => {

    fetchTicket();

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [ticketId]);



  useEffect(() => {

    const userId = Number(user?.id || localStorage.getItem("user_id") || 0);
    const currentRole = (
      user?.role ||
      localStorage.getItem("role") ||
      ""
    ).toLowerCase();
    const canLoadPermissions = ["owner", "manager", "admin", "host"].includes(

      currentRole

    );
    if (!token || !userId || !canLoadPermissions) {

      setPermissionsBundle(null);

      setPermissionsLoading(false);

      return;

    }

    let isActive = true;

    setPermissionsLoading(true);

    getUserPermissions(userId)

      .then((data) => {

        if (!isActive) return;

        setPermissionsBundle(data);

      })

      .catch((err) => {

        console.error(err);

        if (!isActive) return;

        setPermissionsBundle(null);

      })

      .finally(() => {

        if (!isActive) return;

        setPermissionsLoading(false);

      });

    return () => {

      isActive = false;

    };

  }, [token, user?.id, user?.role]);


  useEffect(() => {
    if (!servicesModalOpen || !ticket?.salon_id) return;
    let isActive = true;
    const loadServices = async () => {
      setServiceCatalogLoading(true);
      setServiceCatalogError("");

      try {
        const data = await listServices({ salon_id: ticket.salon_id });
        const services = Array.isArray(data) ? data : [];
        if (!isActive) return;
        setServiceCatalog(services);
      } catch (err) {
        console.error(err);
        setServiceCatalogError("Unable to load services.");
      } finally {

        if (isActive) {

          setServiceCatalogLoading(false);

        }

      }

    };

    loadServices();
    return () => {
      isActive = false;
    };
  }, [servicesModalOpen, ticket?.salon_id]);

  useEffect(() => {
    if (!productsModalOpen || !ticket?.salon_id) return;
    let isActive = true;
    const loadProducts = async () => {
      setProductCatalogLoading(true);
      setProductCatalogError("");
      try {
        const data = await listServices({
          salon_id: ticket.salon_id,
          only_products: true,
        });
        const products = Array.isArray(data) ? data : [];
        if (!isActive) return;
        setProductCatalog(products);
      } catch (err) {
        console.error(err);
        if (!isActive) return;
        setProductCatalogError("Unable to load products.");
      } finally {
        if (isActive) {
          setProductCatalogLoading(false);
        }
      }
    };
    loadProducts();
    return () => {
      isActive = false;
    };
  }, [productsModalOpen, ticket?.salon_id]);

  useEffect(() => {
    if (!serviceCatalog || serviceCatalog.length === 0) return;
    const map = new Map();
    serviceCatalog.forEach((service, idx) => map.set(service.id, idx));
    originalServiceIndexMapRef.current = map;
  }, [serviceCatalog]);

  useEffect(() => {
    if (!productCatalog || productCatalog.length === 0) return;
    const map = new Map();
    productCatalog.forEach((product, idx) => map.set(product.id, idx));
    originalProductIndexMapRef.current = map;
  }, [productCatalog]);


  useEffect(() => {

    let isActive = true;

    const loadAppointment = async () => {

      if (!appointmentId) {

        setAppointmentForTicket(null);

        setApptTypeDraft("Walk in");

        setApptForTypeLoading(false);

        return;

      }

      setApptForTypeLoading(true);

      try {

        const appt = await getAppointment(appointmentId);

        if (!isActive) return;

        setAppointmentForTicket(appt);

        const normalized = normalizeAppointmentType(

          appt?.client_type || appt?.appointment_type || "walk_in"

        );

        setApptTypeDraft(appointmentTypeLabel(normalized) || "Walk in");

      } catch (err) {

        console.error(err);

        if (!isActive) return;

        setAppointmentForTicket(null);

      } finally {

        if (isActive) {

          setApptForTypeLoading(false);

        }

      }

    };

    loadAppointment();

    return () => {

      isActive = false;

    };

  }, [appointmentId]);



  const loadFutureAppointments = useCallback(async () => {

    if (!clientId) {

      setFutureAppts([]);

      setFutureApptsError("");

      setFutureApptsLoading(false);

      return;

    }

    setFutureApptsLoading(true);

    setFutureApptsError("");

    try {

      const data = await listAppointments({

        client_id: clientId,

        only_future: true,

        exclude_cancelled: true,

      });

      const futureList = Array.isArray(data) ? data : [];

      setFutureAppts(futureList.filter((appt) => appt.id !== appointmentId));

    } catch (err) {

      console.error(err);

      setFutureApptsError("Unable to load future appointments.");

    } finally {

      setFutureApptsLoading(false);

    }

  }, [clientId, appointmentId]);



  useEffect(() => {
    loadFutureAppointments();
  }, [loadFutureAppointments]);

  const loadRefunds = useCallback(async () => {
    if (!ticketId) return;
    setRefundsLoading(true);
    setRefundsError("");
    try {
      const data = await listTicketRefunds(ticketId);
      setRefunds(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setRefundsError("Unable to load refunds.");
    } finally {
      setRefundsLoading(false);
    }
  }, [ticketId]);

  const loadRefundContext = useCallback(
    async (targetTicketId) => {
      if (!targetTicketId) return;
      setRefundContextLoading(true);
      setRefundContextError("");
      try {
        const data = await getRefundContext(targetTicketId);
        setRefundContext(data);
      } catch (err) {
        console.error(err);
        setRefundContextError("Unable to load refund details.");
        setRefundContext(null);
      } finally {
        setRefundContextLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!ticketId || !ticket) return;
    const status = (ticket.status || "").toLowerCase();
    const type = (ticket.ticket_type || "").toLowerCase();
    const eligibleStatuses = ["closed", "refunded"];
    const blockedTypes = ["payment_in", "payment_out", "adjustment", "refund"];
    if (!eligibleStatuses.includes(status) || blockedTypes.includes(type)) {
      setRefunds([]);
      return;
    }
    loadRefunds();
  }, [ticketId, ticket, loadRefunds]);

  useEffect(() => {
    const currentTicketId = ticket?.id;
    if (!refundModalOpen || !currentTicketId || !isRefundTicket) return;
    if (refundContext?.refund_ticket_id === currentTicketId) return;
    loadRefundContext(currentTicketId);
  }, [
    refundModalOpen,
    ticket?.id,
    isRefundTicket,
    refundContext?.refund_ticket_id,
    loadRefundContext,
  ]);


  const handleSave = async () => {

    if (!ticketId) return;

    setError("");

    try {

      await updateTicket(ticketId, { notes });

      setIsDirty(false);

      await fetchTicket();

    } catch (err) {

      console.error(err);

      setError(err?.message || "Unable to save ticket");

    }

  };



  const isTicketLocked = useMemo(

    () => ["closed", "void", "refunded"].includes(ticket?.status),

    [ticket?.status]

  );

  const ticketTypeLabel = useMemo(
    () => getTicketTypeLabel(ticket),
    [ticket]
  );
  const ticketTitle = `${ticketTypeLabel} Ticket`;
  const isPaymentTicket = useMemo(
    () => ["payment_in", "payment_out"].includes(normalizedTicketType),
    [normalizedTicketType]
  );
  const isRefundEligible = useMemo(() => {
    if (!ticket) return false;
    const status = (ticket.status || "").toLowerCase();
    if (!["closed", "refunded"].includes(status)) return false;
    if (["payment_in", "payment_out", "adjustment", "refund"].includes(normalizedTicketType)) {
      return false;
    }
    return Number(ticket.grand_total || 0) > 0;
  }, [ticket, normalizedTicketType]);
  const canUseReceiptActions = useMemo(() => {
    if (!ticket) return false;
    const status = (ticket.status || "").toLowerCase();
    if (!["closed", "refunded"].includes(status)) return false;
    if (["payment_in", "payment_out", "adjustment", "refund"].includes(normalizedTicketType)) {
      return false;
    }
    return true;
  }, [ticket, normalizedTicketType]);
  const hasClientEmail = Boolean((receiptClientEmail || "").trim());


  const openServicesModal = () => {
    setServiceCatalogError("");
    setServiceSelection({});
    setEligibleStaffByServiceId({});
    setServiceQuery("");
    setServicesModalOpen(true);
  };

  const closeServicesModal = () => {
    setServicesModalOpen(false);
    setServiceCatalogError("");
    setServiceQuery("");
  };

  const openProductsModal = () => {
    setProductCatalogError("");
    setProductSelection({});
    setProductQuery("");
    setProductsModalOpen(true);
  };

  const closeProductsModal = () => {
    setProductsModalOpen(false);
    setProductCatalogError("");
    setProductQuery("");
  };

  const openGiftCardModal = () => {
    setGiftCardCode("");
    setGiftCardAmount("");
    setGiftCardModalError("");
    setGiftCardModalOpen(true);
  };

  const closeGiftCardModal = () => {
    setGiftCardModalOpen(false);
    setGiftCardModalError("");
    setGiftCardCode("");
    setGiftCardAmount("");
  };

  const getAvailableStaffOptionsForService = useCallback(
    (serviceId) =>
      (eligibleStaffByServiceId[serviceId] || []).filter(
        (staff) => staff?.available !== false
      ),
    [eligibleStaffByServiceId]
  );

  const handleToggleService = (serviceId) => {
    setServiceSelection((prev) => {
      const current = prev[serviceId] || {
        checked: false,
        quantity: 1,
        staffId: null,
      };
      const availableStaff = getAvailableStaffOptionsForService(serviceId);
      if (!availableStaff.length) {
        return prev;
      }
      const nextChecked = !current.checked;
      const currentIsAvailable = availableStaff.some(
        (staff) => Number(staff?.id) === Number(current.staffId)
      );
      const nextStaffId =
        nextChecked && !currentIsAvailable
          ? availableStaff[0].id
          : current.staffId;
      return {
        ...prev,
        [serviceId]: {
          ...current,
          checked: nextChecked,
          staffId: nextStaffId,

        },

      };

    });

  };



  const handleServiceQtyChange = (serviceId, value) => {

    const qty = Number(value || 1);

    setServiceSelection((prev) => ({

      ...prev,

      [serviceId]: {

        ...(prev[serviceId] || { checked: true, staffId: null }),

        checked: true,

        quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,

      },

    }));

  };



  const handleServiceStaffChange = (serviceId, value) => {
    const staffId = value ? Number(value) : null;
    setServiceSelection((prev) => ({
      ...prev,
      [serviceId]: {
        ...(prev[serviceId] || { checked: true, quantity: 1 }),
        checked: true,
        staffId,
      },
    }));
  };

  const handlePickTicketService = (service) => {
    if (!service?.id) return;
    const availableStaff = getAvailableStaffOptionsForService(service.id);
    if (!availableStaff.length) return;
    setServiceSelection((prev) => {
      const current = prev[service.id] || {};
      const existingQty = Number(current.quantity);
      const nextQuantity =
        Number.isFinite(existingQty) && existingQty > 0 ? existingQty : 1;
      const currentIsAvailable = availableStaff.some(
        (staff) => Number(staff?.id) === Number(current.staffId)
      );
      const nextStaffId = currentIsAvailable
        ? current.staffId
        : availableStaff[0].id;
      return {
        ...prev,
        [service.id]: {
          ...current,
          checked: true,
          quantity: nextQuantity,
          staffId: nextStaffId,
        },
      };
    });
    setServiceQuery("");
  };

  const handleToggleProduct = (productId) => {
    setProductSelection((prev) => {
      const current = prev[productId] || { checked: false, quantity: 1 };
      return {
        ...prev,
        [productId]: {
          ...current,
          checked: !current.checked,
          quantity: current.quantity || 1,
        },
      };
    });
  };

  const handleProductQtyChange = (productId, value) => {
    const qty = Number(value || 1);
    setProductSelection((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || { checked: true }),
        checked: true,
        quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
      },
    }));
  };

  const handlePickTicketProduct = (product) => {
    if (!product?.id) return;
    setProductSelection((prev) => {
      const current = prev[product.id] || {};
      const existingQty = Number(current.quantity);
      const nextQuantity =
        Number.isFinite(existingQty) && existingQty > 0 ? existingQty : 1;
      return {
        ...prev,
        [product.id]: {
          ...current,
          checked: true,
          quantity: nextQuantity,
        },
      };
    });
    setProductQuery("");
  };


  const currentAppointmentType =

    appointmentForTicket?.appointment_type ||

    appointmentForTicket?.client_type ||

    ticket?.appointment?.appointment_type ||

    ticket?.appointment_type ||

    "walk_in";

  const currentAppointmentTypeLabel =

    appointmentTypeLabel(normalizeAppointmentType(currentAppointmentType)) ||

    "Walk in";



  useEffect(() => {

    setApptTypeDraft(currentAppointmentTypeLabel || "Walk in");

  }, [currentAppointmentTypeLabel]);



  const handleSaveApptType = async () => {

    if (!ticketId) return;

    setSavingApptType(true);

    setApptTypeError("");

    try {

      const nextValue = normalizeAppointmentType(apptTypeDraft);

      if (appointmentId) {

        const updated = await updateAppointment(appointmentId, {

          appointment_type: nextValue || null,

        }, { allowPast: true });

        setAppointmentForTicket(updated);

        const refreshedLabel =

          appointmentTypeLabel(

            normalizeAppointmentType(updated?.appointment_type)

          ) || "Walk in";

        setApptTypeDraft(refreshedLabel);

      } else {

        const updatedTicket = await updateTicket(ticketId, {

          appointment_type: nextValue || null,

        });

        const refreshedLabel =

          appointmentTypeLabel(

            normalizeAppointmentType(updatedTicket?.appointment_type)

          ) || "Walk in";

        setApptTypeDraft(refreshedLabel);

      }

      await fetchTicket();

      setIsApptTypeModalOpen(false);

    } catch (err) {

      console.error(err);

      const detail = err?.response?.data?.detail || err?.message || "";

      const friendly =

        detail === "PAST_APPOINTMENT_NOT_ALLOWED"

          ? "This appointment can't be edited."

          : detail;

      setApptTypeError(friendly || "Failed to update appointment type.");

    } finally {

      setSavingApptType(false);

    }

  };



  const handleDeleteFutureAppointment = async (apptId) => {

    if (!apptId) return;

    const ok = window.confirm("Delete this appointment?");

    if (!ok) return;

    setDeletingFutureApptId(apptId);

    setFutureApptsError("");

    try {

      await deleteAppointment(apptId);

      const clientId = ticket?.client?.id;

      if (clientId) {

        const data = await listAppointments({

          client_id: clientId,

          only_future: true,

          exclude_cancelled: true,

        });

        const futureList = Array.isArray(data) ? data : [];

        setFutureAppts(

          futureList.filter((appt) => appt.id !== appointmentId)

        );

      }

    } catch (err) {

      console.error(err);

      setFutureApptsError("Unable to delete appointment.");

    } finally {

      setDeletingFutureApptId(null);

    }

  };



  const handleOpenClientModal = async () => {

    const clientId = ticket?.client?.id;

    if (!clientId) return;



    setClientModalError("");

    try {

      const client = await getClientById(clientId);

      setClientToEdit(client);

      setIsClientModalOpen(true);

    } catch (err) {

      setClientModalError(err?.message || "Failed to load client details.");

    }

  };



  const handleOpenServiceLog = async () => {

    if (!ticketClientId) return;

    try {

      const client = await getClientById(ticketClientId);

      setServiceLogClient(client);

      setIsServiceLogOpen(true);

    } catch (err) {

      setError(err?.message || "Failed to load client details.");

    }

  };



  const handleCloseServiceLog = () => {

    setIsServiceLogOpen(false);

    setServiceLogClient(null);

  };



  const handleOpenAppointmentLog = async () => {

    setApptLogs([]);

    setApptLogsError("");

    setIsApptLogOpen(true);



    if (!appointmentId) {

      setApptLogsError("No appointment is linked to this ticket.");

      return;

    }



    setApptLogsLoading(true);

    try {

      const logs = await getAppointmentLogs(appointmentId);

      setApptLogs(Array.isArray(logs) ? logs : []);

    } catch (err) {

      console.error(err);

      setApptLogsError(

        err?.response?.data?.detail ||

          err?.message ||

          "Unable to load appointment log."

      );

    } finally {

      setApptLogsLoading(false);

    }

  };



  const handleCloseAppointmentLog = () => {

    setIsApptLogOpen(false);

    setApptLogs([]);

    setApptLogsError("");

    setApptLogsLoading(false);

  };



  const handleSaveClientFromModal = async (payload) => {

    if (!clientToEdit?.id) return;



    await updateClient(clientToEdit.id, payload);

    const refreshed = await getClientById(clientToEdit.id);

    setClientToEdit(refreshed);

    setTicket((prev) => (prev ? { ...prev, client: refreshed } : prev));

  };



  const handleAddServices = async () => {
    if (!ticketId) return;
    if (isTicketLocked) {
      setError("Ticket is closed/void and cannot be edited.");
      return;
    }

    const items = Object.entries(serviceSelection)

      .filter(([, data]) => data?.checked)

      .map(([serviceId, data]) => ({

        service_id: Number(serviceId),

        quantity: Number(data?.quantity || 1),

        staff_id: data?.staffId ?? null,

      }))

      .filter((entry) => entry.service_id);



    const missingStaff = items.find((item) => !item.staff_id);
    if (missingStaff) {
      setServiceCatalogError("Select a staff member for each service.");
      return;
    }
    const unavailableStaff = items.find((item) => {
      const staffOptions = eligibleStaffByServiceId[item.service_id] || [];
      const selected = staffOptions.find(
        (staff) => Number(staff.id) === Number(item.staff_id)
      );
      return selected && selected.available === false;
    });
    if (unavailableStaff) {
      setServiceCatalogError(
        "Selected technician is unavailable at the appointment time. Please choose another technician."
      );
      return;
    }

    if (!items.length) {
      setServiceCatalogError("Select at least one service.");
      return;
    }


    setError("");

    showBanner("");
    try {
      await addTicketServices(ticketId, { items });
      closeServicesModal();
      await fetchTicket();
      emitScheduleRevenueRefresh();
      showBanner("Services added successfully.");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      if (detail?.code === "STAFF_NOT_AVAILABLE") {
        setError(
          "This technician is not working or is blocked off during the appointment time. Please choose another technician."
        );
      } else {
        setError(
          detail?.message || detail || err?.message || "Unable to add services"
        );
      }
    }
  };

  const handleAddProducts = async () => {
    if (!ticketId) return;
    if (isTicketLocked) {
      setError("Ticket is closed/void and cannot be edited.");
      return;
    }
    setProductCatalogError("");
    const items = Object.entries(productSelection)
      .filter(([, data]) => data?.checked)
      .map(([productId, data]) => ({
        product_id: Number(productId),
        quantity: Number(data?.quantity || 1),
      }))
      .filter((entry) => entry.product_id);

    if (!items.length) {
      setProductCatalogError("Select at least one product.");
      return;
    }

    setError("");
    showBanner("");
    try {
      await addTicketProducts(ticketId, { items });
      closeProductsModal();
      await fetchTicket();
      emitScheduleRevenueRefresh();
      showBanner("Products added successfully.");
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to add products");
    }
  };

  const handleAddGiftCard = async () => {
    if (!ticketId) return;
    if (isTicketLocked) {

      setError("Ticket is closed/void and cannot be edited.");

      return;

    }

    const code = (giftCardCode || "").trim();
    const amount = Number(giftCardAmount || 0);
    if (!code || amount <= 0) {
      setGiftCardModalError("Enter a gift card code and amount.");
      return;
    }
    setError("");
    setGiftCardModalError("");
    showBanner("");
    try {
      await addTicketGiftCard(ticketId, { code, amount, quantity: 1 });
      closeGiftCardModal();
      await fetchTicket();
      emitScheduleRevenueRefresh();
      showBanner("Gift card added.");
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Unable to add gift card";
      setGiftCardModalError(detail);
    }
  };


  const serviceItems = useMemo(
    () =>
      (ticket?.items || []).filter(
        (item) => (item.line_type || "").toLowerCase() === "service"
      ),
    [ticket]
  );

  const productItems = useMemo(
    () =>
      (ticket?.items || []).filter(
        (item) => (item.line_type || "").toLowerCase() === "product"
      ),
    [ticket]
  );

  const giftCardItems = useMemo(
    () =>
      (ticket?.items || []).filter(
        (item) => (item.line_type || "").toLowerCase() === "gift_card"
      ),
    [ticket]
  );
  const otherItems = useMemo(
    () =>
      (ticket?.items || []).filter((item) => {
        const type = (item.line_type || "").toLowerCase();
        return type !== "service" && type !== "gift_card" && type !== "product";
      }),
    [ticket]
  );

  const serviceItemServiceIds = useMemo(() => {
    const ids = new Set();
    for (const item of ticket?.items || []) {
      if ((item?.line_type || "").toLowerCase() !== "service") continue;
      const serviceId = Number(item?.service_id);
      if (Number.isFinite(serviceId) && serviceId > 0) {
        ids.add(serviceId);
      }
    }
    return Array.from(ids);
  }, [ticket?.items]);

  const refreshServiceStaffAvailability = useCallback(
    async (serviceIds, { showError = false } = {}) => {
      const ticketValue = Number(ticket?.id || 0);
      const uniqueIds = Array.from(
        new Set(
          (serviceIds || [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0)
        )
      );
      if (!ticketValue || !uniqueIds.length) {
        setEligibleStaffByServiceId({});
        return;
      }
      setServiceStaffAvailabilityLoading(true);
      try {
        const payload = { service_ids: uniqueIds };
        const data = await getTicketServiceStaffAvailability(ticketValue, payload);
        const byServiceRaw =
          data?.by_service && typeof data.by_service === "object"
            ? data.by_service
            : {};
        const nextStaffByService = {};
        for (const serviceId of uniqueIds) {
          const key = String(serviceId);
          const entry = byServiceRaw[key] || byServiceRaw[serviceId] || {};
          const staffRows = Array.isArray(entry?.staff) ? entry.staff : [];
          nextStaffByService[serviceId] = staffRows.map((row) => ({
            id: Number(row?.id),
            display_name: row?.display_name || `Staff #${row?.id}`,
            available: row?.available !== false,
            reason: row?.reason || null,
          }));
        }
        setEligibleStaffByServiceId(nextStaffByService);
        if (showError) {
          setServiceCatalogError("");
        }
      } catch (err) {
        console.error(err);
        setEligibleStaffByServiceId({});
        if (showError) {
          setServiceCatalogError("Unable to load staff availability.");
        }
      } finally {
        setServiceStaffAvailabilityLoading(false);
      }
    },
    [ticket?.id]
  );

  useEffect(() => {
    const serviceIds = [...serviceItemServiceIds];
    if (servicesModalOpen) {
      for (const service of serviceCatalog || []) {
        const serviceId = Number(service?.id);
        if (Number.isFinite(serviceId) && serviceId > 0) {
          serviceIds.push(serviceId);
        }
      }
    }
    refreshServiceStaffAvailability(serviceIds, { showError: servicesModalOpen });
  }, [
    refreshServiceStaffAvailability,
    serviceCatalog,
    serviceItemServiceIds,
    servicesModalOpen,
  ]);

  useEffect(() => {
    setServiceSelection((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [serviceId, row] of Object.entries(prev)) {
        if (!row?.checked) continue;
        const availableStaff = (eligibleStaffByServiceId[serviceId] || []).filter(
          (staff) => staff?.available !== false
        );
        if (!availableStaff.length) continue;
        const currentIsAvailable = availableStaff.some(
          (staff) => Number(staff?.id) === Number(row?.staffId)
        );
        if (currentIsAvailable) continue;
        changed = true;
        next[serviceId] = {
          ...row,
          staffId: availableStaff[0].id,
        };
      }
      return changed ? next : prev;
    });
  }, [eligibleStaffByServiceId]);

  const servicesSubtotal = useMemo(
    () =>
      serviceItems.reduce((sum, item) => sum + getLineSubtotal(item), 0),
    [serviceItems]
  );
  const productsSubtotal = useMemo(
    () =>
      productItems.reduce((sum, item) => sum + getLineSubtotal(item), 0),
    [productItems]
  );
  const invalidServiceAssignmentsByItemId = useMemo(() => {
    const map = {};
    for (const item of serviceItems) {
      const serviceId = Number(item?.service_id);
      const staffId = Number(item?.staff_id);
      if (!Number.isFinite(serviceId) || serviceId <= 0) continue;
      if (!Number.isFinite(staffId) || staffId <= 0) continue;
      const options = eligibleStaffByServiceId[serviceId] || [];
      const matched = options.find((staff) => Number(staff?.id) === staffId);
      if (matched && matched.available === false) {
        map[item.id] = matched.reason || "Technician unavailable at this time.";
      }
    }
    return map;
  }, [eligibleStaffByServiceId, serviceItems]);
  const hasInvalidServiceAssignments =
    Object.keys(invalidServiceAssignmentsByItemId).length > 0;
  const selectedServiceIdSet = useMemo(() => {
    const ids = Object.entries(serviceSelection)
      .filter(([, value]) => value?.checked)
      .map(([id]) => Number(id))
      .filter(Boolean);
    return new Set(ids);
  }, [serviceSelection]);
  const selectedProductIdSet = useMemo(() => {
    const ids = Object.entries(productSelection)
      .filter(([, value]) => value?.checked)
      .map(([id]) => Number(id))
      .filter(Boolean);
    return new Set(ids);
  }, [productSelection]);

  const filteredServiceCatalog = useMemo(() => {
    const list = Array.isArray(serviceCatalog) ? serviceCatalog : [];
    const query = normalizeForSearch(serviceQuery).trim();
    const base = query
      ? list.filter((svc) =>
          normalizeForSearch(svc?.name).includes(query)
        )
      : list;
    return reorderServicesSelectedFirst(
      base,
      selectedServiceIdSet,
      originalServiceIndexMapRef.current
    );
  }, [serviceCatalog, serviceQuery, selectedServiceIdSet]);

  const groupedServices = useMemo(() => {
    const groups = {};
    (filteredServiceCatalog || []).forEach((svc) => {
      const key =
        svc.category ||
        svc.group ||
        svc.category_name ||
        svc.type ||
        "Services";
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(svc);
    });
    return Object.entries(groups).map(([key, items]) => ({
      key,
      items,
    }));
  }, [filteredServiceCatalog]);

  const filteredProductCatalog = useMemo(() => {
    const list = Array.isArray(productCatalog) ? productCatalog : [];
    const query = normalizeForSearch(productQuery).trim();
    const base = query
      ? list.filter((product) => {
          const nameMatch = normalizeForSearch(product?.name).includes(query);
          const skuMatch = normalizeForSearch(product?.sku).includes(query);
          return nameMatch || skuMatch;
        })
      : list;
    return reorderServicesSelectedFirst(
      base,
      selectedProductIdSet,
      originalProductIndexMapRef.current
    );
  }, [productCatalog, productQuery, selectedProductIdSet]);
  const isClosed = ["closed", "refunded"].includes(
    (ticket?.status || "").toLowerCase()
  );
  const salonTicketTaxExemptEnabled = Boolean(
    ticket?.salon_ticket_tax_exempt_enabled
  );
  const tipsFromRows = useMemo(() => {

    const rows = Array.isArray(ticket?.tips) ? ticket.tips : [];

    return rows.reduce((sum, row) => sum + Number(row?.amount || 0), 0);

  }, [ticket]);

  const tipsTotal = useMemo(() => {

    const storedValue =

      ticket?.total_tips ??

      ticket?.tips_total ??

      ticket?.tipsTotal ??

      ticket?.summary?.tips;

    const num = storedValue == null ? Number.NaN : Number(storedValue);

    if (Number.isFinite(num)) return num;

    return Number.isFinite(tipsFromRows) ? tipsFromRows : 0;

  }, [ticket, tipsFromRows]);

  const totalWithTips = useMemo(() => {

    const base = Number(ticket?.grand_total || 0);

    return base + tipsTotal;

  }, [ticket?.grand_total, tipsTotal]);

  const changeReturned = useMemo(() => {

    if (!isClosed) return 0;

    const raw =

      ticket?.change_returned ?? ticket?.changeReturned ?? ticket?.summary?.change_returned;

    const num = raw == null ? Number.NaN : Number(raw);

    if (Number.isFinite(num)) return num;

    const paid = Number(ticket?.amount_paid || 0);

    const due = Number(ticket?.grand_total || 0) + tipsTotal;

    const diff = paid - due;

    return diff > 0 ? diff : 0;

  }, [ticket, tipsTotal, isClosed]);

  const paymentMethodRows = useMemo(() => {
    if (!isClosed) return [];
    const order = ["cash", "debit", "credit", "gift_card", "house", "other"];

    const labelMap = {

      cash: "Cash",

      debit: "Debit",

      credit: "Credit",

      gift_card: "Gift Card",

      house: "House",

      other: "Other",

    };

    const normalize = (value) => {

      const key = String(value || "other")

        .toLowerCase()

        .replace(/\s+/g, "_");

      if (key === "giftcard") return "gift_card";

      if (key === "in_house" || key === "inhouse") return "house";

      return key;

    };

    const raw =

      ticket?.payments_by_method ??

      ticket?.paymentsByMethod ??

      ticket?.summary?.payments_by_method;

    let entries = [];

    if (raw && typeof raw === "object") {

      entries = Object.entries(raw);

    } else {

      const totals = {};

      const payments = Array.isArray(ticket?.payments) ? ticket.payments : [];

      payments.forEach((payment) => {

        if (payment?.is_pending) return;

        if (payment?.is_applied === false) return;

        const method = normalize(payment?.method);

        const amount = Number(payment?.amount || 0);

        if (!Number.isFinite(amount)) return;

        totals[method] = (totals[method] || 0) + amount;

      });

      entries = Object.entries(totals);

    }

    return entries

      .map(([method, amount]) => {

        const normalized = normalize(method);

        return {

          method: normalized,

          label: labelMap[normalized] || "Other",

          amount: Number(amount || 0),

        };

      })

      .filter((row) => Number.isFinite(row.amount) && row.amount !== 0)

      .sort((a, b) => {
        const aIdx = order.indexOf(a.method);
        const bIdx = order.indexOf(b.method);
        return (aIdx === -1 ? order.length : aIdx) - (bIdx === -1 ? order.length : bIdx);
      });
  }, [ticket, isClosed]);
  const totalRefunded = useMemo(() => {
    return (refunds || []).reduce((sum, entry) => {
      const amount = Number(entry?.total_refund_amount || 0);
      return Number.isFinite(amount) ? sum + amount : sum;
    }, 0);
  }, [refunds]);
  const remainingRefundable = useMemo(() => {
    const total = Number(ticket?.grand_total || 0);
    const remaining = total - totalRefunded;
    return remaining > 0 ? remaining : 0;
  }, [ticket, totalRefunded]);


  if (loading && !ticket) {

    return (

      <div className="ticketDetailPageShell min-h-screen bg-slate-50/60 py-6">

        <div className="ticketDetailPageWide ticket-detail-page-compact">

      <div className="max-w-5xl px-4 text-sm text-slate-600">
        Loading ticket...

      </div>

        </div>

      </div>
    );

  }



  if (!ticket && error) {

    return (

      <div className="ticketDetailPageShell min-h-screen bg-slate-50/60 py-6">

        <div className="ticketDetailPageWide ticket-detail-page-compact">

      <div className="max-w-5xl px-4">
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">

          {error}

        </div>

        <button

          type="button"

          className="text-sm text-emerald-600 underline"

          onClick={() => navigate("/tickets")}

        >

          Back to Tickets

        </button>

      </div>

        </div>

      </div>
    );

  }



  if (!ticket) return null;



  const clientName =

    ticket?.client?.display_name ||

    ticket?.client?.full_name ||

    ticket?.client?.name ||

    "Walk-in";

  const ticketClientId = ticket?.client?.id ?? ticket?.client_id ?? null;

  const ticketClient =

    ticket?.client || (ticketClientId ? { id: ticketClientId } : null);

  const ticketSalonId = ticket?.salon_id ?? null;

  const ticketDateStr = ticket?.ticket_date || "";
  const rawAppointmentModalDate =
    appointmentForTicket?.start_time ||
    appointmentForTicket?.start_at ||
    appointmentForTicket?.scheduled_at ||
    appointmentForTicket?.date ||
    appointmentForTicket?.appointment_date ||
    ticketDateStr;
  const appointmentModalDateStr =
    formatDateOnlyYYYYMMDD(rawAppointmentModalDate) || ticketDateStr || "";
  const ticketTimeStr = formatTimeInput(ticket?.opened_at, ticketTimezone);
  const prebookDisabled =

    !appointmentForTicket?.id || !ticketClientId || !ticketSalonId;

  const shouldShowTips =

    tipsTotal > 0 || (Array.isArray(ticket?.tips) && ticket.tips.length > 0);

  const handleBackToAppointment = () => {
    if (!appointmentId) return;
    const params = new URLSearchParams();
    if (appointmentModalDateStr) {
      params.set("date", appointmentModalDateStr);
    }
    params.set("highlightAppointmentId", String(appointmentId));
    navigate(`/manager/schedule?${params.toString()}`);
  };

  const headerActions = (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {appointmentId ? (
          <button
            type="button"
            className="rounded-full border border-white/40 bg-white/10 px-4 py-2 text-sm md:text-base font-semibold text-white transition hover:bg-white/20"
            onClick={handleBackToAppointment}
          >
            Back to Appointment
          </button>
        ) : null}
        <button
        type="button"

        className="rounded-full border border-white/40 bg-white/10 px-4 py-2 text-sm md:text-base font-semibold text-white transition hover:bg-white/20"

        onClick={() => navigate("/tickets")}

      >

        Back to Tickets

      </button>
      </div>
    );

    const ticketActions = (

      <div className="flex flex-wrap items-center justify-end gap-2">

          {!isClosed && !isPaymentTicket && !isRefundTicket && (
            <button
              type="button"
              onClick={() => navigate(`/tickets/${ticketId}/collect-payment`)}
              disabled={hasInvalidServiceAssignments}
              title={
                hasInvalidServiceAssignments
                  ? "Reassign unavailable technicians before collecting payment."
                  : undefined
              }
              className="rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-sm md:text-base font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Collect Payment
            </button>
          )}
        {isDirty && (

          <button

            type="button"

            onClick={handleSave}

            className="rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white transition hover:bg-slate-800"

          >

            Save Ticket

          </button>

        )}

      </div>

    );



  const openRemoveLineItemConfirm = (item) => {
    if (!ticket || !item) return;
    setConfirmMessage("Remove this line item from the ticket?");
    setConfirmAction(() => async () => {
      setConfirmBusy(true);
      try {
        await deleteTicketItem(ticket.id, item.id);
        await fetchTicket();
        emitScheduleRevenueRefresh();
        showBanner("Item removed.");
        setConfirmOpen(false);
        setConfirmAction(null);
        setConfirmMessage("");
      } catch (err) {
        console.error(err);
        const detail =
          err?.response?.data?.detail || err?.message || "Failed to remove item";
        setError(detail);
      } finally {
        setConfirmBusy(false);
      }
    });
    setConfirmOpen(true);
  };

  const handleDeleteItem = (item) => {
    openRemoveLineItemConfirm(item);
  };

  const handleServiceStaffCommit = async (item, value) => {
    if (!ticket || !item || isTicketLocked) return;
    const nextStaffId = value ? Number(value) : null;
    const currentStaffId = item?.staff_id ? Number(item.staff_id) : null;
    if (nextStaffId === currentStaffId) return;

    setStaffAssignSaving((prev) => ({ ...prev, [item.id]: true }));
    setError("");
    try {
      const updated = await updateTicketItem(ticket.id, item.id, {
        staff_id: nextStaffId,
      });
      setTicket(updated);
      emitScheduleRevenueRefresh();
      showBanner("Technician updated.");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      if (detail?.code === "STAFF_NOT_AVAILABLE") {
        setError(
          "This technician is not working or is blocked off during the appointment time. Please choose another technician."
        );
      } else {
        setError(
          detail?.message || detail || err?.message || "Unable to update technician."
        );
      }
    } finally {
      setStaffAssignSaving((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleUnitPriceChange = (itemId, value) => {
    setUnitPriceDrafts((prev) => ({ ...prev, [itemId]: value }));
  };


  const resetUnitPriceDraft = (itemId) => {

    setUnitPriceDrafts((prev) => {

      const next = { ...prev };

      delete next[itemId];

      return next;

    });

  };



  const handleUnitPriceCommit = async (item) => {

    if (!ticket || !item || isTicketLocked) return;

    const draft = unitPriceDrafts[item.id];

    if (draft == null) return;

    const trimmed = String(draft).trim();

    if (trimmed === "" || trimmed === "-" || trimmed === "." || trimmed === "-.") {

      resetUnitPriceDraft(item.id);

      return;

    }

    const nextValue = Number(draft);

    const currentValue = Number(item.unit_price || 0);

    if (!Number.isFinite(nextValue)) {

      resetUnitPriceDraft(item.id);

      setError("Enter a valid unit price.");

      return;

    }

    if (Math.abs(nextValue - currentValue) < 0.0001) {

      resetUnitPriceDraft(item.id);

      return;

    }

    setUnitPriceSaving((prev) => ({ ...prev, [item.id]: true }));

    setError("");

    try {

      const updated = await updateTicketItem(ticket.id, item.id, {
        unit_price: nextValue,
      });
      setTicket(updated);
      emitScheduleRevenueRefresh();
      showBanner("Price updated.");
      resetUnitPriceDraft(item.id);
    } catch (err) {

      console.error(err);

      setError(err?.message || "Unable to update unit price.");

      resetUnitPriceDraft(item.id);

    } finally {

      setUnitPriceSaving((prev) => ({ ...prev, [item.id]: false }));

    }

  };



  const openDiscountModal = (item) => {

    if (!item || isTicketLocked) return;

    setDiscountItem(item);

    setDiscountType(item.discount_type || "percent");

    setDiscountValue(

      item.discount_value != null ? String(item.discount_value) : ""

    );

    setDiscountModalOpen(true);

  };



  const closeDiscountModal = () => {

    setDiscountModalOpen(false);

    setDiscountItem(null);

    setDiscountValue("");

    setDiscountType("percent");

    setDiscountSaving(false);

  };



  const formatDiscountLabel = (item) => {

    if (!item?.discount_type || item.discount_value == null) return null;

    const value = Number(item.discount_value);

    if (!Number.isFinite(value) || value <= 0) return null;

    if (item.discount_type === "percent") {

      return `${value.toFixed(2)}%`;

    }

    return formatMoney(value);

  };



  const handleApplyDiscount = async () => {

    if (!ticket || !discountItem) return;

    const value = Number(discountValue || 0);

    if (!Number.isFinite(value) || value < 0) {

      setError("Enter a valid discount value.");

      return;

    }

    if (discountType === "percent" && value > 100) {

      setError("Discount percent must be between 0 and 100.");

      return;

    }

    setDiscountSaving(true);

    setError("");

    try {

      const payload =

        value <= 0

          ? { discount_type: null, discount_value: null }

          : { discount_type: discountType, discount_value: value };

      const updated = await updateTicketItem(ticket.id, discountItem.id, payload);
      setTicket(updated);
      emitScheduleRevenueRefresh();
      showBanner("Discount updated.");
      closeDiscountModal();
    } catch (err) {

      console.error(err);

      setError(err?.message || "Unable to update discount.");

    } finally {

      setDiscountSaving(false);

    }

  };



  const openTaxConfirm = () => {

    if (isClosed) return;
    if (!salonTicketTaxExemptEnabled) {
      setError(
        "Ticket tax exempt is disabled for this salon. A super admin must enable it in the salon setup wizard first."
      );
      return;
    }

    setTaxConfirmOpen(true);

  };



  const closeTaxConfirm = () => {

    setTaxConfirmOpen(false);

  };



  const closeTaxMessage = () => {

    setTaxMessageOpen(false);

  };



  const handleSetTaxExempt = async (isTaxExempt) => {

    if (!ticket || taxSubmitting || isClosed) return;

    setTaxSubmitting(true);

    setError("");

    try {

      const updated = await setTicketTaxExempt(ticket.id, isTaxExempt);

      setTicket(updated);

      setTaxConfirmOpen(false);

      setTaxMessageOpen(true);

    } catch (err) {

      console.error(err);

      setError(
        err?.response?.data?.detail || err?.message || "Unable to update tax status."
      );

    } finally {

      setTaxSubmitting(false);

    }

  };



  const handleDeleteTicket = async () => {

    if (!ticket || deleteSubmitting) return;

    setDeleteSubmitting(true);

    setError("");

    try {

      await deleteTicket(ticket.id);

      setDeleteModalOpen(false);

      navigate("/tickets");

    } catch (err) {

      console.error(err);

      const detail =

        err?.response?.data?.detail || err?.message || "Unable to delete ticket.";

      setError(detail);

    } finally {

      setDeleteSubmitting(false);

    }

  };



  const openDeleteModal = () => {

    if (isClosed) return;

    setDeleteModalOpen(true);

  };



  const handleReopenTicket = async () => {
    if (!ticket || reopenSubmitting) return;
    setReopenSubmitting(true);
    setError("");
    try {
      const shouldOpenRefundModal = reopenIntent === "edit_refund";
      await reopenTicket(ticket.id);
      setReopenModalOpen(false);
      await fetchTicket();
      await listTicketPayments(ticket.id);
      emitScheduleRevenueRefresh();
      showBanner("Ticket re-opened.");
      if (shouldOpenRefundModal) {
        setRefundModalOpen(true);
        loadRefundContext(ticket.id);
      } else {
        setRefundModalOpen(false);
        setRefundContext(null);
        setRefundContextError("");
      }
      setReopenIntent(null);
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail || err?.message || "Unable to re-open ticket.";
      setError(detail);
    } finally {
      setReopenSubmitting(false);
    }
  };

  const handlePrintTicketPdf = async () => {
    if (!ticket?.id || ticketPdfLoading) return;
    setTicketPdfLoading(true);
    setError("");
    try {
      const { blob, filename } = await downloadTicketReceiptPdf(ticket.id);
      const pdfBlob =
        blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" });
      const objectUrl = window.URL.createObjectURL(pdfBlob);
      const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
      if (!opened) {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = filename || `ticket-${ticket.id}-receipt.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
      showBanner("Ticket PDF opened.");
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail || err?.message || "Unable to generate ticket PDF.";
      setError(detail);
    } finally {
      setTicketPdfLoading(false);
    }
  };

  const handleEmailTicket = async () => {
    if (!ticket?.id || ticketEmailSending || !hasClientEmail) return;
    setTicketEmailSending(true);
    setError("");
    try {
      const result = await emailTicketReceipt(ticket.id);
      if (result?.email_sent) {
        showBanner(`Ticket emailed to ${result.email_to || "client"}.`);
      } else {
        setError("Ticket email was not sent. Check SMTP email settings.");
      }
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail || err?.message || "Unable to email ticket.";
      setError(detail);
    } finally {
      setTicketEmailSending(false);
    }
  };

  const handleEditRefund = () => {
    if (!ticket) return;
    const status = (ticket.status || "").toLowerCase();
    if (status === "closed") {
      if (!canReopenTicket) {
        setError("Not authorized to reopen tickets.");
        return;
      }
      setReopenIntent("edit_refund");
      setReopenModalOpen(true);
      return;
    }
    setReopenIntent(null);
    setRefundModalOpen(true);
    loadRefundContext(ticket.id);
  };

  const handleRefundCreated = async (refundResult) => {
    setRefundModalOpen(false);
    await fetchTicket();
    await loadRefunds();
    showBanner("Refund created.", refundResult?.refund_ticket_id);
  };

  const handleRefundUpdated = async () => {
    setRefundModalOpen(false);
    await fetchTicket();
    await loadRefunds();
    showBanner("Refund updated.");
  };


  return (

    <div className="ticketDetailPageShell min-h-screen bg-slate-50/60 py-6">
      <div className="ticketDetailPageWide ticket-detail-page-compact">
          <SBINPageCard

            title={ticketTitle}

            actions={headerActions}

          >

            <div className="space-y-3 sm:space-y-4">
              {error && (

                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">

                  {error}

                </div>

              )}

              {banner && !error && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800 flex flex-wrap items-center justify-between gap-3">
                  <span>{banner}</span>
                  {refundReceiptId && (
                    <button
                      type="button"
                      onClick={() => navigate(`/tickets/${refundReceiptId}`)}
                      className="rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-semibold text-emerald-700 hover:border-emerald-300"
                    >
                      View refund receipt
                    </button>
                  )}
                </div>
              )}
              {(isDirty || !isClosed) && ticketActions}

      {servicesModalOpen && (
        <SBINModal
          isOpen={servicesModalOpen}
          onClose={closeServicesModal}
          title="Add Service To Ticket"
          size="lg"
          panelClassName="overflow-hidden h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-16px)] max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-16px)] rounded-2xl sm:rounded-3xl sm:h-[90vh] sm:max-h-[90vh]"
          overlayClassName="items-start px-2 py-[calc(8px+env(safe-area-inset-top,0px))] sm:items-center sm:px-4 sm:py-6"
          headerClassName="px-4 py-3 sm:px-6 sm:py-4"
          bodyClassName="px-4 py-4 sm:px-6 sm:py-6"
          footerClassName="px-4 py-3 sm:px-6 sm:py-4"
          footer={
            <div className="flex justify-end gap-2">

              <button

                type="button"

                className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"

                onClick={closeServicesModal}

              >

                Close

              </button>

              <button

                type="button"

                onClick={handleAddServices}

                disabled={isTicketLocked}

                className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"

              >

                Add Services

              </button>

            </div>
          }

        >

            <div className="space-y-3 sm:space-y-4">
            {serviceCatalogError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {serviceCatalogError}
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                Search services...
              </label>
              <ServiceSearch
                services={serviceCatalog}
                value={serviceQuery}
                onChange={setServiceQuery}
                onPick={handlePickTicketService}
              />
            </div>
              <div className="border border-slate-200 rounded-2xl max-h-[48vh] sm:max-h-[60vh] overflow-y-auto">
              {serviceCatalogLoading && (
                <div className="px-3 py-3 text-sm text-slate-600">
                  Loading services...

                </div>

              )}

              {!serviceCatalogLoading && groupedServices.length === 0 && (

                <div className="px-3 py-3 text-sm text-slate-600">

                  No services found.

                </div>

              )}

              {!serviceCatalogLoading &&

                groupedServices.map((group) => (

                  <div key={group.key} className="border-t first:border-t-0">

                    <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 bg-slate-50">

                      {group.key}

                    </div>

                    <div className="divide-y divide-slate-100">

                      {group.items.map((svc) => {
                        const eligibleStaff = eligibleStaffByServiceId[svc.id] || [];
                        const availableStaff = eligibleStaff.filter(
                          (staff) => staff?.available !== false
                        );
                        const hasEligibleStaff = eligibleStaff.length > 0;
                        const hasAvailableStaff = availableStaff.length > 0;
                        const selectedStaffId = serviceSelection[svc.id]?.staffId || "";
                        const selectedStaff = eligibleStaff.find(
                          (staff) => Number(staff?.id) === Number(selectedStaffId)
                        );
                        return (
                          <div
                            key={svc.id}
                            className="flex items-center justify-between px-3 py-2 gap-3"
                          >
                            <label className="flex items-center gap-3 text-sm text-slate-800">

                              <input
                                type="checkbox"
                                checked={serviceSelection[svc.id]?.checked || false}
                                onChange={() => handleToggleService(svc.id)}
                                disabled={!hasAvailableStaff}
                                className="h-4 w-4 rounded border border-slate-300 text-slate-900 disabled:opacity-60"
                              />
                              <span className="flex flex-col">
                                <span>{svc.name || "Service"}</span>
                                {!hasEligibleStaff && (
                                  <span className="text-[11px] text-slate-400">
                                    No eligible staff
                                  </span>
                                )}
                                {hasEligibleStaff && !hasAvailableStaff && (
                                  <span className="text-[11px] text-amber-600">
                                    No technicians available at this appointment time
                                  </span>
                                )}
                                {selectedStaff?.available === false &&
                                  selectedStaff?.reason && (
                                    <span className="text-[11px] text-rose-600">
                                      {selectedStaff.reason}
                                    </span>
                                  )}
                              </span>
                            </label>
                            <div className="flex items-center gap-3">
                              <select
                                value={selectedStaffId}
                                onChange={(e) =>
                                  handleServiceStaffChange(svc.id, e.target.value)
                                }
                                disabled={!hasEligibleStaff}
                                className="w-40 rounded-xl border border-slate-300 px-2 py-1 text-xs focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:bg-slate-100 disabled:text-slate-400"
                              >
                                <option value="" disabled>
                                  Select staff
                                </option>
                                {eligibleStaff.map((staff) => (
                                  <option
                                    key={staff.id}
                                    value={staff.id}
                                    disabled={staff.available === false}
                                  >
                                    {staff.display_name || `Staff #${staff.id}`}
                                    {staff.available === false && staff.reason
                                      ? ` - ${staff.reason}`
                                      : ""}
                                  </option>
                                ))}
                              </select>
                              <span className="text-xs text-slate-500">
                                {formatMoney(svc.base_price)}
                              </span>

                              <input

                                type="number"

                                min="1"

                                value={serviceSelection[svc.id]?.quantity || 1}
                                onChange={(e) =>
                                  handleServiceQtyChange(svc.id, e.target.value)
                                }
                                disabled={!hasAvailableStaff}
                                className="w-16 rounded-xl border border-slate-300 px-2 py-1 text-xs text-right focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                              />
                            </div>
                          </div>
                        );

                      })}

                    </div>

                  </div>

                ))}

            </div>

          </div>

        </SBINModal>
      )}

      {productsModalOpen && (
        <SBINModal
          isOpen={productsModalOpen}
          onClose={closeProductsModal}
          title="Add Products To Ticket"
          size="lg"
          panelClassName="overflow-hidden h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-16px)] max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-16px)] rounded-2xl sm:rounded-3xl sm:h-[90vh] sm:max-h-[90vh]"
          overlayClassName="items-start px-2 py-[calc(8px+env(safe-area-inset-top,0px))] sm:items-center sm:px-4 sm:py-6"
          headerClassName="px-4 py-3 sm:px-6 sm:py-4"
          bodyClassName="px-4 py-4 sm:px-6 sm:py-6"
          footerClassName="px-4 py-3 sm:px-6 sm:py-4"
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={closeProductsModal}
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleAddProducts}
                disabled={isTicketLocked}
                className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
              >
                Add Products
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            {productCatalogError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {productCatalogError}
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                Search products...
              </label>
              <ServiceSearch
                services={productCatalog}
                value={productQuery}
                onChange={setProductQuery}
                onPick={handlePickTicketProduct}
                placeholder="Search products..."
              />
            </div>
              <div className="border border-slate-200 rounded-2xl max-h-[48vh] sm:max-h-[60vh] overflow-y-auto">
              {productCatalogLoading && (
                <div className="px-3 py-3 text-sm text-slate-600">
                  Loading products...
                </div>
              )}
              {!productCatalogLoading && filteredProductCatalog.length === 0 && (
                <div className="px-3 py-3 text-sm text-slate-600">
                  No products found.
                </div>
              )}
              {!productCatalogLoading &&
                filteredProductCatalog.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between px-3 py-2 gap-3 border-t first:border-t-0"
                  >
                    <label className="flex items-center gap-3 text-sm text-slate-800">
                      <input
                        type="checkbox"
                        checked={productSelection[product.id]?.checked || false}
                        onChange={() => handleToggleProduct(product.id)}
                        className="h-4 w-4 rounded border border-slate-300 text-slate-900"
                      />
                      <span className="flex flex-col">
                        <span>{product.name || "Product"}</span>
                        {product.sku && (
                          <span className="text-[11px] text-slate-400">
                            SKU: {product.sku}
                          </span>
                        )}
                      </span>
                    </label>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500">
                        {formatMoney(product.base_price)}
                      </span>
                      <input
                        type="number"
                        min="1"
                        value={productSelection[product.id]?.quantity || 1}
                        onChange={(e) =>
                          handleProductQtyChange(product.id, e.target.value)
                        }
                        className="w-16 rounded-xl border border-slate-300 px-2 py-1 text-xs text-right focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </SBINModal>
      )}

      {giftCardModalOpen && (
        <SBINModal
          isOpen={giftCardModalOpen}
          onClose={closeGiftCardModal}
          title="Add Gift Card"
          size="sm"
          fitContent={true}
          footer={
            <div className="flex justify-end gap-2">
              <button

                type="button"

                className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"

                onClick={closeGiftCardModal}

              >

                Close

              </button>

              <button

                type="button"

                onClick={handleAddGiftCard}

                disabled={isTicketLocked}

                className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"

              >

                Add Gift Card

              </button>

            </div>

          }

        >
          <div className="space-y-3">
            {giftCardModalError && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {giftCardModalError}
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs text-slate-600">Gift card code</label>
              <input
                type="text"
                value={giftCardCode}
                onChange={(e) => {
                  setGiftCardCode(e.target.value);
                  if (giftCardModalError) setGiftCardModalError("");
                }}
                className={`w-full rounded-xl border px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 ${
                  giftCardModalError ? "border-red-300" : "border-slate-300"
                }`}
                placeholder="Enter code"
              />
            </div>
            <div className="space-y-1">

              <label className="text-xs text-slate-600">Amount</label>

              <input

                type="number"

                min="0"

                step="0.01"

                value={giftCardAmount}

                onChange={(e) => setGiftCardAmount(e.target.value)}

                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"

                placeholder="0.00"

              />

            </div>

          </div>

        </SBINModal>

      )}



      {discountModalOpen && (
        <SBINModal
          isOpen={discountModalOpen}
          onClose={closeDiscountModal}
          title="Discount Item"
          size="sm"
          fitContent={true}
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={closeDiscountModal}
                disabled={discountSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyDiscount}
                disabled={discountSaving}
                className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
              >
                {discountSaving ? "Saving..." : "Discount"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              How much would you like to discount this item?
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm text-slate-700">Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  placeholder="0"
                  disabled={discountSaving}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm text-slate-700">Type</label>
                <select
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  disabled={discountSaving}
                >
                  <option value="percent">Percent</option>
                  <option value="dollar">Dollar</option>
                </select>
              </div>
            </div>
          </div>
        </SBINModal>
      )}


      {taxConfirmOpen && (

        <SBINModal

          isOpen={taxConfirmOpen}

          onClose={closeTaxConfirm}

          title="Confirm"

          size="sm"

          fitContent={true}

          footer={

            <div className="flex justify-end gap-2">

              <button

                type="button"

                className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"

                onClick={() => handleSetTaxExempt(false)}

                disabled={taxSubmitting}

              >

                No

              </button>

              <button

                type="button"

                onClick={() => handleSetTaxExempt(true)}

                disabled={taxSubmitting || isClosed}

                className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"

              >

                {taxSubmitting ? "Saving..." : "Yes"}

              </button>

            </div>

          }

        >

          <p className="text-sm text-slate-700">

            Make this ticket Tax Exempt?

          </p>

        </SBINModal>

      )}



      {taxMessageOpen && (
        <SBINModal
          isOpen={taxMessageOpen}
          onClose={closeTaxMessage}
          title="Message"
          size="sm"

          fitContent={true}

          footer={

            <div className="flex justify-end">

              <button

                type="button"

                className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600"

                onClick={closeTaxMessage}

              >

                OK

              </button>

            </div>

          }

        >

          <p className="text-sm text-slate-700">Ticket has been updated.</p>
        </SBINModal>
      )}

      {confirmOpen && (
        <ConfirmModal
          open={confirmOpen}
          title="Confirm"
          message={confirmMessage}
          confirmText="Yes"
          cancelText="No"
          isBusy={confirmBusy}
          onClose={() => {
            if (confirmBusy) return;
            setConfirmOpen(false);
            setConfirmAction(null);
            setConfirmMessage("");
          }}
          onConfirm={async () => {
            if (!confirmAction || confirmBusy) return;
            await confirmAction();
          }}
        />
      )}

      {!isClosed && deleteModalOpen && (
        <DeleteTicketModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleDeleteTicket}
          isSubmitting={deleteSubmitting}

        />

      )}

      {reopenModalOpen && (
        <ReopenTicketModal
          isOpen={reopenModalOpen}
          onClose={() => {
            setReopenModalOpen(false);
            setReopenIntent(null);
          }}
          onConfirm={handleReopenTicket}
          isSubmitting={reopenSubmitting}
          message={
            reopenIntent === "edit_refund"
              ? "Are you sure you want to re-open this ticket to edit the refund?"
              : "Are you sure you want to re-open this ticket?"
          }
        />
      )}
        {refundModalOpen && (
          <RefundTicketModal
            isOpen={refundModalOpen}
            ticketId={ticket?.id}
            mode={isRefundTicket ? "edit" : "create"}
            originalTicketId={refundContext?.original_ticket_id}
            refundTicketId={isRefundTicket ? ticket?.id : null}
            initialContext={refundContext}
            onClose={() => setRefundModalOpen(false)}
            onRefundCreated={handleRefundCreated}
            onRefundUpdated={handleRefundUpdated}
          />
        )}


      <AppointmentLogModal

        isOpen={isApptLogOpen}

        onClose={handleCloseAppointmentLog}

        logs={apptLogs}

        loading={apptLogsLoading}

        salonTimezone={ticketTimezone}
        error={apptLogsError}

      />



      {isServiceLogOpen && (

        <NewClientModal

          isOpen={isServiceLogOpen}

          mode="edit"

          initialClient={serviceLogClient}

          salons={[]}

          canEdit={false}

          preferredSalonId={ticket?.salon_id ? Number(ticket.salon_id) : null}

          initialTab="services"

          onClose={handleCloseServiceLog}

          onSave={null}

        />

      )}

      <NewClientModal

        isOpen={isClientModalOpen}

        mode="edit"

        initialClient={clientToEdit}

        salons={[]}

        canEdit={canEditClients}

        preferredSalonId={ticket?.salon_id ? Number(ticket.salon_id) : null}

        onClose={() => {

          setIsClientModalOpen(false);

          setClientToEdit(null);

          setClientModalError("");

        }}

        onSave={handleSaveClientFromModal}

      />

      {isPrebookOpen && (

        <PreBookNextVisitModal

          isOpen={isPrebookOpen}

          onClose={() => setIsPrebookOpen(false)}

          anchorAppointment={appointmentForTicket}

          client={ticketClient}

          staff={appointmentForTicket?.staff_member || appointmentForTicket?.staff}

          dateStr={ticketDateStr}

          slotTime={ticketTimeStr}

          clientAppointmentsToday={[]}

          ticketServiceItems={serviceItems}

          staffOptions={[]}

          salonId={ticketSalonId}

          onCreated={async () => {

            setIsPrebookOpen(false);

            await fetchTicket();

            await loadFutureAppointments();

          }}

        />

      )}

      {isFutureModalOpen && (

        <SBINModal

          isOpen={isFutureModalOpen}

          onClose={() => setIsFutureModalOpen(false)}

          title="Future Appointments"

          size="lg"

          footer={

            <div className="flex justify-end">

              <button

                type="button"

                className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"

                onClick={() => setIsFutureModalOpen(false)}

              >

                Close

              </button>

            </div>

          }

        >

          <div className="space-y-3">

            {futureApptsError && (

              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">

                {futureApptsError}

              </div>

            )}

            {futureApptsLoading ? (

              <div className="text-sm text-slate-500">Loading...</div>

            ) : futureAppts.length === 0 ? (

              <div className="text-sm text-slate-500">

                No future appointments found.

              </div>

            ) : (

              <div className="border border-slate-200 rounded-2xl overflow-hidden">

                <div className="grid grid-cols-[2fr_1.6fr_1.2fr_1.2fr_52px] gap-2 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">

                  <span>Service</span>

                  <span>When</span>

                  <span>With</span>

                  <span>Scheduled By</span>

                  <span></span>

                </div>

                <div className="divide-y divide-slate-200">

                  {futureAppts.map((appt) => (

                    <div

                      key={appt.id}

                      className="grid grid-cols-[2fr_1.6fr_1.2fr_1.2fr_52px] gap-2 px-4 py-3 text-sm text-slate-700 items-center"

                    >

                      <span className="text-slate-900">

                        {appt.service?.name || "Service"}

                      </span>

                      <span>
                        {formatDateTimeRange(
                          appt.start_time,
                          appt.end_time,
                          ticketTimezone
                        )}
                      </span>
                      <span>

                        {appt.staff_member?.display_name ||

                          appt.staff_member?.name ||

                          `Staff #${appt.staff_member?.id || appt.staff_id}`}

                      </span>

                      <span>{appt.scheduled_by_name || "--"}</span>

                      <button

                        type="button"

                        onClick={() => handleDeleteFutureAppointment(appt.id)}

                        disabled={deletingFutureApptId === appt.id}

                        className="text-slate-400 hover:text-red-600 disabled:opacity-50"

                        aria-label="Delete appointment"

                      >

                        <svg

                          xmlns="http://www.w3.org/2000/svg"

                          viewBox="0 0 20 20"

                          fill="currentColor"

                          className="h-4 w-4"

                        >

                          <path

                            fillRule="evenodd"

                            d="M7.5 3a1 1 0 0 0-1 1v1H4.75a.75.75 0 0 0 0 1.5h.5l.8 10.2a2 2 0 0 0 1.996 1.8h4.908a2 2 0 0 0 1.996-1.8l.8-10.2h.5a.75.75 0 0 0 0-1.5H13.5V4a1 1 0 0 0-1-1h-5Zm1.5 2V4h2v1h-2Z"

                            clipRule="evenodd"

                          />

                        </svg>

                      </button>

                    </div>

                  ))}

                </div>

              </div>

            )}

          </div>

        </SBINModal>

      )}

      {isApptTypeModalOpen && (

        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6">

          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">

            <div className="flex items-center justify-between bg-rose-500 px-6 py-4">

              <h3 className="text-base font-semibold text-white">

                Change Appointment Type

              </h3>

              <ModalCloseX

                onClick={() => setIsApptTypeModalOpen(false)}

              />

            </div>



            <div className="px-6 py-4 space-y-3">

              {apptTypeError ? (

                <div className="text-sm text-red-600">{apptTypeError}</div>

              ) : (

                <div className="text-sm text-slate-500">

                  Choose the appointment type and click Update.

                </div>

              )}



              <select

                value={apptTypeDraft}

                onChange={(e) => setApptTypeDraft(e.target.value)}

                className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-base md:text-lg bg-white focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"

              >

                {APPOINTMENT_TYPES.map((label) => (

                  <option key={label} value={label}>

                    {label}

                  </option>

                ))}

              </select>

            </div>



            <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">

              <button

                type="button"

                onClick={() => setIsApptTypeModalOpen(false)}

                className="rounded-full border border-slate-300 px-6 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400"

                disabled={savingApptType}

              >

                Cancel

              </button>

              <button

                type="button"

                onClick={handleSaveApptType}

                className="rounded-full px-6 py-2 text-sm font-semibold text-white transition bg-slate-900 hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"

                disabled={savingApptType}

              >

                {savingApptType ? "Updating..." : "Update"}

              </button>

            </div>

          </div>

        </div>

      )}



      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        <div className="lg:col-span-2 space-y-4">

          <section

            className={`rounded-2xl border p-5 space-y-4 ${

              isClosed ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"

            }`}

          >

            <div className="flex flex-wrap items-start justify-between gap-3">

              <div>

                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">

                    {ticketTitle}

                  </p>

                <h2 className="text-2xl font-semibold text-slate-900">

                  {ticket?.client ? (

                    <button

                      type="button"

                      onClick={handleOpenClientModal}

                      className="text-sky-600 hover:text-sky-700 hover:underline text-2xl md:text-3xl font-semibold leading-tight"

                    >

                      {clientName}

                    </button>

                  ) : (

                    clientName

                  )}

                  <button

                    type="button"

                    onClick={() => {

                      setApptTypeError("");

                      setIsApptTypeModalOpen(true);

                    }}

                    className="ml-2 text-slate-500 hover:text-slate-700 hover:underline text-lg md:text-xl font-medium"

                    title="Change appointment type"

                  >

                    ({apptForTypeLoading ? "Loading..." : currentAppointmentTypeLabel || "Walk in"})

                  </button>

                </h2>

                {clientModalError ? (

                  <div className="mt-1 text-sm text-red-600">

                    {clientModalError}

                  </div>

                ) : null}

                <p className="text-sm text-slate-600">

                  Ticket #{ticket.ticket_number}

                </p>

                {futureAppts.length > 0 && (

                  <div className="mt-1 text-sm text-slate-600">

                    has {futureAppts.length}{" "}

                    <button

                      type="button"

                      onClick={() => setIsFutureModalOpen(true)}

                      className="text-sky-600 hover:text-sky-700 hover:underline font-medium"

                    >

                      future appointment

                      {futureAppts.length > 1 ? "s" : ""}

                    </button>

                    .

                  </div>

                )}

                <div className="mt-1 flex items-center gap-2 text-sm text-slate-600">

                  <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold">

                    {ticket.status}

                  </span>

                  {ticket.salon_id ? (

                    <span>Salon #{ticket.salon_id}</span>

                  ) : null}

                  {isClosed && (

                    <span className="inline-flex px-2 py-0.5 rounded-full bg-emerald-500 text-white text-sm font-semibold">

                      Closed

                    </span>

                  )}

                </div>

              </div>
              <div className="text-right text-base text-slate-600">
                <div>
                  Ticket Date: {formatDateOnlyYYYYMMDD(ticket?.ticket_date)}
                </div>
              </div>
            </div>
            <div className="space-y-1">

              <label className="text-xs uppercase tracking-wide text-slate-500">

                Notes

              </label>

              <textarea

                value={notes}

                onChange={(e) => {

                  setNotes(e.target.value);

                  setIsDirty(true);

                }}

                rows={3}

                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base md:text-lg focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"

                placeholder="Add ticket notes..."

              ></textarea>

            </div>

            <div className="flex flex-wrap items-center gap-2">

              <button

                type="button"

                onClick={handleOpenServiceLog}

                disabled={!ticketClientId}

                className="rounded-full border border-slate-300 px-4 py-2 text-sm md:text-base font-semibold text-slate-700 transition hover:border-slate-400 disabled:opacity-50"

              >

                Service Log

              </button>

              <button

                type="button"

                onClick={handleOpenAppointmentLog}

                disabled={!appointmentId}

                className="rounded-full border border-slate-300 px-4 py-2 text-sm md:text-base font-semibold text-slate-700 transition hover:border-slate-400 disabled:opacity-50"

              >

                Appointment Log

              </button>

              <button

                type="button"

                onClick={() => setIsPrebookOpen(true)}

                disabled={prebookDisabled}

                className="rounded-full border border-slate-300 px-4 py-2 text-sm md:text-base font-semibold text-slate-700 transition hover:border-slate-400 disabled:opacity-50"

              >

                Pre-book next visit

              </button>

              {!permissionsLoading && canDeleteTicket && !isClosed && (

                <button

                  type="button"

                  onClick={openDeleteModal}

                  className="rounded-full border border-rose-200 px-4 py-2 text-sm md:text-base font-semibold text-rose-600 transition hover:border-rose-300"

                >

                  Delete Ticket

                </button>

              )}

              {!permissionsLoading &&
                canReopenTicket &&
                (ticket?.status || "").toLowerCase() === "closed" && (
                <button
                  type="button"
                  onClick={() => {
                    setReopenIntent("reopen_ticket");
                    setReopenModalOpen(true);
                  }}
                  className="rounded-full border border-slate-300 px-4 py-2 text-sm md:text-base font-semibold text-slate-700 transition hover:border-slate-400"
                >
                  Re-open Ticket
                </button>
              )}
              {!permissionsLoading && canRefundTicket && isRefundTicket && (
                <button
                  type="button"
                  onClick={handleEditRefund}
                  className="rounded-full border border-emerald-200 px-4 py-2 text-sm md:text-base font-semibold text-emerald-700 transition hover:border-emerald-300"
                >
                  Edit Refund
                </button>
              )}
              {canUseReceiptActions && (
                <button
                  type="button"
                  onClick={handlePrintTicketPdf}
                  disabled={ticketPdfLoading}
                  className="rounded-full border border-slate-300 px-4 py-2 text-sm md:text-base font-semibold text-slate-700 transition hover:border-slate-400 disabled:opacity-50"
                >
                  {ticketPdfLoading ? "Preparing PDF..." : "Print PDF"}
                </button>
              )}
              {canUseReceiptActions && (
                <button
                  type="button"
                  onClick={handleEmailTicket}
                  disabled={ticketEmailSending || !hasClientEmail}
                  title={
                    hasClientEmail ? undefined : "Add an email address to the client profile first."
                  }
                  className="rounded-full border border-slate-300 px-4 py-2 text-sm md:text-base font-semibold text-slate-700 transition hover:border-slate-400 disabled:opacity-50"
                >
                  {ticketEmailSending ? "Emailing..." : "Email Ticket"}
                </button>
              )}
              {!permissionsLoading && canRefundTicket && isRefundEligible && (
                <button
                  type="button"
                  onClick={() => setRefundModalOpen(true)}
                  className="rounded-full border border-emerald-200 px-4 py-2 text-sm md:text-base font-semibold text-emerald-700 transition hover:border-emerald-300"
                >
                  Refund
                </button>
              )}
            </div>

          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Client Feedback
              </div>
            </div>
            {ticketReviewLoading && (
              <div className="text-sm text-slate-500">Loading feedback...</div>
            )}
            {ticketReviewError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {ticketReviewError}
              </div>
            )}
            {ticketReviewResendError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {ticketReviewResendError}
              </div>
            )}
            {ticketReviewResendNotice && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                {ticketReviewResendNotice}
              </div>
            )}
            {!ticketReviewLoading && !ticketReviewError && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="grid grid-cols-1 gap-3 text-sm text-slate-600 md:grid-cols-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Last request
                      </div>
                      <div className="font-semibold text-slate-800">
                        {ticketReviewLastRequestLabel}
                      </div>
                      {ticketReview?.sent_to_email && (
                        <div className="text-xs text-slate-500">
                          Sent to {ticketReview.sent_to_email}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Completion
                      </div>
                      <div className="font-semibold text-slate-800">
                        {ticketReviewCompletedLabel}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Send status
                      </div>
                      <div
                        className={`font-semibold ${
                          ticketReview?.send_status === "failed"
                            ? "text-rose-600"
                            : "text-slate-800"
                        }`}
                      >
                        {ticketReview?.review_request_id
                          ? formatSendStatus(ticketReview.send_status)
                          : "Not sent"}
                      </div>
                      {ticketReview?.send_error && (
                        <div className="text-xs text-rose-600">
                          {ticketReview.send_error}
                        </div>
                      )}
                    </div>
                  </div>
                  {canResendReview && !ticketReview?.submitted_at && (
                    <button
                      type="button"
                      onClick={handleResendReview}
                      disabled={ticketReviewResendLoading}
                      className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    >
                      {ticketReviewResendLoading
                        ? "Sending..."
                        : ticketReview?.review_request_id
                          ? "Resend Review Request"
                          : "Send Review Request"}
                    </button>
                  )}
                </div>
                {ticketReview?.review_request_id && (ticketReview.submitted_at ? (
                  <>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Submitted
                      </span>
                      <span>
                        {formatDateTime(ticketReview.submitted_at, ticketTimezone)}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Overall
                        </div>
                        {renderStarRow(ticketReview.overall_rating)}
                      </div>
                      <div className="space-y-1">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Salon
                        </div>
                        {renderStarRow(ticketReview.salon_rating)}
                      </div>
                      <div className="space-y-1">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Reception
                        </div>
                        {renderStarRow(ticketReview.reception_rating)}
                      </div>
                      <div className="space-y-1">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Service
                        </div>
                        {renderStarRow(ticketReview.service_rating)}
                      </div>
                      <div className="space-y-1">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Staff
                        </div>
                        {renderStarRow(ticketReview.staff_rating)}
                      </div>
                      {ticketReview.online_booking_rating != null && (
                        <div className="space-y-1">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Online booking
                          </div>
                          {renderStarRow(ticketReview.online_booking_rating)}
                        </div>
                      )}
                    </div>
                    {ticketReview.comment && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 whitespace-pre-line">
                        {ticketReview.comment}
                      </div>
                    )}
                    {Array.isArray(ticketReview.technician_ratings) &&
                      ticketReview.technician_ratings.length > 0 && (
                        <div className="space-y-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Technician ratings
                          </div>
                          <div className="space-y-3">
                            {ticketReview.technician_ratings.map((rating) => (
                              <div
                                key={rating.technician_user_id}
                                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
                              >
                                <div className="text-sm font-semibold text-slate-900">
                                  {rating.technician_name ||
                                    `Technician #${rating.technician_user_id}`}
                                </div>
                                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div className="space-y-1">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      Service
                                    </div>
                                    {renderStarRow(rating.service_rating)}
                                  </div>
                                  <div className="space-y-1">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      Staff
                                    </div>
                                    {renderStarRow(rating.staff_rating)}
                                  </div>
                                </div>
                                {rating.comment && (
                                  <div className="mt-2 text-sm text-slate-700 whitespace-pre-line">
                                    {rating.comment}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-slate-600">
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Follow-up allowed
                        </span>
                        <div>{ticketReview.allow_contact ? "Yes" : "No"}</div>
                      </div>
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Public review link shown
                        </span>
                        <div>{ticketReview.show_google_cta ? "Yes" : "No"}</div>
                      </div>
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Primary technician ID
                        </span>
                        <div>
                          {ticketReview.technician_user_id ?? "Unassigned"}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-1 text-sm text-slate-600">
                    <div>
                      Feedback request sent{" "}
                      {ticketReview.sent_at
                        ? formatDateTime(ticketReview.sent_at, ticketTimezone)
                        : "-- pending email"}
                    </div>
                    {ticketReview.sent_to_email && (
                      <div className="text-xs text-slate-500">
                        Sent to {ticketReview.sent_to_email}
                      </div>
                    )}
                    {ticketReview.reminder_count != null && (
                      <div className="text-xs text-slate-500">
                        Reminders sent: {ticketReview.reminder_count}
                      </div>
                    )}
                    {ticketReview.next_reminder_at && (
                      <div className="text-xs text-slate-500">
                        Next reminder:{" "}
                        {formatDateTime(
                          ticketReview.next_reminder_at,
                          ticketTimezone
                        )}
                      </div>
                    )}
                    <div className="text-xs text-slate-500">
                      Primary technician ID:{" "}
                      {ticketReview.technician_user_id ?? "Unassigned"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

            {otherItems.length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                <div className="flex items-center justify-between">

                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">

                    Items

                  </div>

                </div>

                <div className="border border-slate-200 rounded-2xl overflow-hidden">

                  <div className="grid grid-cols-[1fr,110px] gap-2 px-3 py-2 text-sm font-semibold uppercase tracking-wide text-slate-500 bg-slate-50">

                    <span>Item</span>

                    <span className="text-right">Amount</span>

                  </div>

                  {otherItems.map((item) => (

                    <div

                      key={item.id}

                      className="grid grid-cols-[1fr,110px] gap-2 px-3 py-3 border-t border-slate-100 items-center"

                    >

                      <div className="text-base text-slate-900">

                        {item.description || "Item"}

                      </div>

                      <div className="text-right text-base text-slate-700">

                        {formatMoney(getItemAmount(item))}

                      </div>

                    </div>

                  ))}

                </div>

              </section>
            )}



            {!isPaymentTicket && (

              <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">

            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Services
                </div>
                <div className="text-sm text-slate-500">
                  Ticket Date: {formatDateOnlyYYYYMMDD(ticket?.ticket_date)}
                </div>
              </div>
              <button

                type="button"

                onClick={openServicesModal}

                disabled={isTicketLocked}

                className="h-8 w-8 rounded-full bg-emerald-600 text-white text-lg leading-none flex items-center justify-center hover:bg-emerald-700 disabled:opacity-60"

                aria-label="Add service"

              >

                +
              </button>
            </div>
            {hasInvalidServiceAssignments && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Some services are assigned to technicians who are unavailable at
                this appointment time. Reassign them before collecting payment.
              </div>
            )}
            <div className="border border-slate-200 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
              <div className="min-w-[560px]">
              <div className="grid grid-cols-[1fr,90px,70px,90px,90px,32px] gap-2 px-3 py-2 text-sm font-semibold uppercase tracking-wide text-slate-500 bg-slate-50">
                <span>Service</span>

                <span>Discount</span>

                <span className="text-right">Qty</span>

                <span className="text-right">Price</span>

                <span className="text-right">Total</span>

                <span></span>

              </div>

              {serviceItems.length === 0 && (

                <div className="px-3 py-4 text-base text-slate-500">

                  No services added yet.

                </div>

              )}

              {serviceItems.map((item) => {
                const discountLabel = formatDiscountLabel(item);
                const serviceId = Number(item?.service_id);
                const baseStaffOptions =
                  Number.isFinite(serviceId) && serviceId > 0
                    ? eligibleStaffByServiceId[serviceId] || []
                    : [];
                const currentStaffId = item?.staff_id ? Number(item.staff_id) : null;
                const currentStaffLabel =
                  item.staff?.display_name ||
                  item.staff?.full_name ||
                  (currentStaffId ? `Staff #${currentStaffId}` : "Unassigned");
                const hasCurrentStaffOption = baseStaffOptions.some(
                  (staff) => Number(staff?.id) === currentStaffId
                );
                const staffOptions = hasCurrentStaffOption
                  ? baseStaffOptions
                  : currentStaffId
                  ? [
                      ...baseStaffOptions,
                      {
                        id: currentStaffId,
                        display_name: currentStaffLabel,
                        available: false,
                        reason: "Not eligible for this service",
                      },
                    ]
                  : baseStaffOptions;
                const assignmentWarning = invalidServiceAssignmentsByItemId[item.id];
                return (
                  <div
                    key={item.id}
                    className="grid grid-cols-[1fr,90px,70px,90px,90px,32px] gap-2 px-3 py-3 border-t border-slate-100 items-center"
                  >
                    <div>
                      <div className="text-base text-slate-900">
                        {item.description}
                      </div>
                      {!isTicketLocked && staffOptions.length > 0 ? (
                        <div className="mt-1">
                          <select
                            value={currentStaffId ? String(currentStaffId) : ""}
                            onChange={(e) =>
                              handleServiceStaffCommit(item, e.target.value)
                            }
                            disabled={
                              staffAssignSaving[item.id] ||
                              serviceStaffAvailabilityLoading
                            }
                            className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs text-slate-600 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:bg-slate-50"
                          >
                            <option value="">Unassigned</option>
                            {staffOptions.map((staff) => (
                              <option
                                key={`${item.id}-${staff.id}`}
                                value={staff.id}
                                disabled={staff.available === false}
                              >
                                {staff.display_name || `Staff #${staff.id}`}
                                {staff.available === false && staff.reason
                                  ? ` - ${staff.reason}`
                                  : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        (item.staff?.display_name ||
                          item.staff?.full_name ||
                          item.staff_id) && (
                          <div className="text-sm text-slate-500">
                            by{" "}
                            {item.staff?.display_name ||
                              item.staff?.full_name ||
                              `Staff #${item.staff_id}`}
                          </div>
                        )
                      )}
                      {assignmentWarning && (
                        <div className="mt-1 text-xs text-rose-600">
                          Technician unavailable: {assignmentWarning}
                        </div>
                      )}
                    </div>
                    <button

                      type="button"

                      onClick={() => openDiscountModal(item)}

                      disabled={isTicketLocked}

                      className={`text-left text-sm underline ${

                        discountLabel

                          ? "text-emerald-600 font-semibold"

                          : "text-slate-500 hover:text-slate-700"

                      } disabled:opacity-50`}

                    >

                      {discountLabel ? `Discount: ${discountLabel}` : "Discount"}

                    </button>

                    <div className="text-right text-base text-slate-700">

                      {item.quantity}

                    </div>

                    <div className="flex justify-end">

                      <input

                        type="number"

                        step="0.01"

                        value={

                          unitPriceDrafts[item.id] ??

                          String(item.unit_price ?? "")

                        }

                        onChange={(e) =>

                          handleUnitPriceChange(item.id, e.target.value)

                        }

                        onBlur={() => handleUnitPriceCommit(item)}

                        onKeyDown={(e) => {

                          if (e.key === "Enter") {

                            e.currentTarget.blur();

                          }

                        }}

                        disabled={isTicketLocked || unitPriceSaving[item.id]}

                        className="w-20 rounded-xl border border-slate-300 px-2 py-2 text-base text-right focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:bg-slate-50"

                      />

                    </div>

                    <div className="text-right text-base text-slate-700">

                      {formatMoney(getLineSubtotal(item))}

                    </div>

                    <button

                      type="button"

                      onClick={() => handleDeleteItem(item)}

                      disabled={isTicketLocked}

                      className="text-slate-400 hover:text-red-600 disabled:opacity-50"

                      aria-label="Remove service"

                    >

                      <svg

                        xmlns="http://www.w3.org/2000/svg"

                        viewBox="0 0 20 20"

                        fill="currentColor"

                        className="h-4 w-4"

                      >

                        <path

                          fillRule="evenodd"

                          d="M7.5 3a1 1 0 0 0-1 1v1H4.75a.75.75 0 0 0 0 1.5h.5l.8 10.2a2 2 0 0 0 1.996 1.8h4.908a2 2 0 0 0 1.996-1.8l.8-10.2h.5a.75.75 0 0 0 0-1.5H13.5V4a1 1 0 0 0-1-1h-5Zm1.5 2V4h2v1h-2Z"

                          clipRule="evenodd"

                        />

                      </svg>

                    </button>

                  </div>

                );

              })}

            </div>

              </div>
              </div>
            <div className="flex justify-end text-base font-semibold text-slate-700">
              Services: {formatMoney(servicesSubtotal)}
            </div>
              </section>
            )}

            {!isPaymentTicket && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Products
                    </div>
                    <div className="text-sm text-slate-500">
                      Ticket Date: {formatDateOnlyYYYYMMDD(ticket?.ticket_date)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={openProductsModal}
                    disabled={isTicketLocked}
                    className="h-8 w-8 rounded-full bg-emerald-600 text-white text-lg leading-none flex items-center justify-center hover:bg-emerald-700 disabled:opacity-60"
                    aria-label="Add product"
                  >
                    +
                  </button>
                </div>
                <div className="border border-slate-200 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                  <div className="min-w-[560px]">
                  <div className="grid grid-cols-[1fr,90px,70px,90px,90px,32px] gap-2 px-3 py-2 text-sm font-semibold uppercase tracking-wide text-slate-500 bg-slate-50">
                    <span>Product</span>
                    <span>Discount</span>
                    <span className="text-right">Qty</span>
                    <span className="text-right">Price</span>
                    <span className="text-right">Total</span>
                    <span></span>
                  </div>
                  {productItems.length === 0 && (
                    <div className="px-3 py-4 text-base text-slate-500">
                      No products added yet.
                    </div>
                  )}
                  {productItems.map((item) => {
                    const discountLabel = formatDiscountLabel(item);
                    return (
                      <div
                        key={item.id}
                        className="grid grid-cols-[1fr,90px,70px,90px,90px,32px] gap-2 px-3 py-3 border-t border-slate-100 items-center"
                      >
                        <div>
                          <div className="text-base text-slate-900">
                            {item.description}
                          </div>
                          {(item.staff?.display_name ||
                            item.staff?.full_name ||
                            item.staff_id) && (
                            <div className="text-sm text-slate-500">
                              by{" "}
                              {item.staff?.display_name ||
                                item.staff?.full_name ||
                                `Staff #${item.staff_id}`}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => openDiscountModal(item)}
                          disabled={isTicketLocked}
                          className={`text-left text-sm underline ${
                            discountLabel
                              ? "text-emerald-600 font-semibold"
                              : "text-slate-500 hover:text-slate-700"
                          } disabled:opacity-50`}
                        >
                          {discountLabel ? `Discount: ${discountLabel}` : "Discount"}
                        </button>
                        <div className="text-right text-base text-slate-700">
                          {item.quantity}
                        </div>
                        <div className="flex justify-end">
                          <input
                            type="number"
                            step="0.01"
                            value={
                              unitPriceDrafts[item.id] ??
                              String(item.unit_price ?? "")
                            }
                            onChange={(e) =>
                              handleUnitPriceChange(item.id, e.target.value)
                            }
                            onBlur={() => handleUnitPriceCommit(item)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.currentTarget.blur();
                              }
                            }}
                            disabled={isTicketLocked || unitPriceSaving[item.id]}
                            className="w-20 rounded-xl border border-slate-300 px-2 py-2 text-base text-right focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:bg-slate-50"
                          />
                        </div>
                        <div className="text-right text-base text-slate-700">
                          {formatMoney(getLineSubtotal(item))}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteItem(item)}
                          disabled={isTicketLocked}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-50"
                          aria-label="Remove product"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="h-4 w-4"
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.5 3a1 1 0 0 0-1 1v1H4.75a.75.75 0 0 0 0 1.5h.5l.8 10.2a2 2 0 0 0 1.996 1.8h4.908a2 2 0 0 0 1.996-1.8l.8-10.2h.5a.75.75 0 0 0 0-1.5H13.5V4a1 1 0 0 0-1-1h-5Zm1.5 2V4h2v1h-2Z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
                  </div>
                  </div>
                <div className="flex justify-end text-base font-semibold text-slate-700">
                  Products: {formatMoney(productsSubtotal)}
                </div>
              </section>
            )}

            {!isPaymentTicket && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Gift Cards
              </div>

              <button

                type="button"

                onClick={openGiftCardModal}

                disabled={isTicketLocked}

                className="h-8 w-8 rounded-full bg-emerald-600 text-white text-lg leading-none flex items-center justify-center hover:bg-emerald-700 disabled:opacity-60"

                aria-label="Add gift card"

              >

                +

              </button>

            </div>

            <div className="border border-slate-200 rounded-2xl overflow-hidden">

              <div className="overflow-x-auto">
              <div className="min-w-[500px]">
              <div className="grid grid-cols-[1fr,70px,90px,90px,32px] gap-2 px-3 py-2 text-sm font-semibold uppercase tracking-wide text-slate-500 bg-slate-50">
                <span>Gift Card</span>

                <span className="text-right">Qty</span>

                <span className="text-right">Amount</span>

                <span className="text-right">Total</span>

                <span></span>

              </div>

              {giftCardItems.length === 0 && (

                <div className="px-3 py-4 text-base text-slate-500">

                  No gift cards added yet.

                </div>

              )}

              {giftCardItems.map((item) => (

                <div

                  key={item.id}

                  className="grid grid-cols-[1fr,70px,90px,90px,32px] gap-2 px-3 py-3 border-t border-slate-100 items-center"

                >

                  <div className="text-base text-slate-900">

                    Code: {item.gift_card_code || "-"}

                  </div>

                  <div className="text-right text-base text-slate-700">

                    {item.quantity}

                  </div>

                  <div className="text-right text-base text-slate-700">

                    {formatMoney(item.unit_price)}

                  </div>

                  <div className="text-right text-base text-slate-700">

                    {formatMoney(item.line_total)}

                  </div>

                  <button

                    type="button"

                    onClick={() => handleDeleteItem(item)}

                    disabled={isTicketLocked}

                    className="text-slate-400 hover:text-red-600 disabled:opacity-50"

                    aria-label="Remove gift card"

                  >

                    <svg

                      xmlns="http://www.w3.org/2000/svg"

                      viewBox="0 0 20 20"

                      fill="currentColor"

                      className="h-4 w-4"

                    >

                      <path

                        fillRule="evenodd"

                        d="M7.5 3a1 1 0 0 0-1 1v1H4.75a.75.75 0 0 0 0 1.5h.5l.8 10.2a2 2 0 0 0 1.996 1.8h4.908a2 2 0 0 0 1.996-1.8l.8-10.2h.5a.75.75 0 0 0 0-1.5H13.5V4a1 1 0 0 0-1-1h-5Zm1.5 2V4h2v1h-2Z"

                        clipRule="evenodd"

                      />

                    </svg>

                  </button>

                </div>

              ))}

            </div>

              </div>
              </div>
              </section>

            )}

        </div>



        <div className="space-y-4">

          <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Summary
            </div>
            <div className="space-y-2 text-base">

              <div className="flex justify-between text-slate-700">

                <span className="text-slate-500">Subtotal</span>

                <span className="font-medium">{formatMoney(ticket.sub_total)}</span>

              </div>

              <div className="flex justify-between text-slate-700">

                <div className="flex items-center gap-2">

                  <button

                    type="button"

                    onClick={openTaxConfirm}

                    disabled={isClosed || !salonTicketTaxExemptEnabled}

                    className={`text-base ${

                      isClosed || !salonTicketTaxExemptEnabled

                        ? "text-slate-400 cursor-not-allowed"

                        : "text-sky-700 hover:underline"

                    }`}
                    title={
                      salonTicketTaxExemptEnabled
                        ? undefined
                        : "A super admin must enable ticket tax exemption for this salon first."
                    }

                  >

                    Taxes

                  </button>

                </div>

                <span className="font-medium">{formatMoney(ticket.tax_total)}</span>

              </div>

              {ticket.subtotal_ex_tax != null && (

                <div className="flex justify-between text-sm text-slate-500">

                  <span>Subtotal (ex tax)</span>

                  <span>{formatMoney(ticket.subtotal_ex_tax)}</span>

                </div>

              )}

              {ticket.taxes?.length > 0 && (

                <div className="mt-1 space-y-1 text-sm text-slate-600">

                  <div className="text-sm font-semibold text-slate-500">

                    Tax breakdown

                  </div>

                  {ticket.taxes.map((tax) => (

                    <div key={tax.name} className="flex justify-between">

                      <span>

                        {tax.name} ({formatRatePercent(tax.rate_percent)})

                      </span>

                      <span>{formatMoney(tax.amount)}</span>

                    </div>

                  ))}

                </div>

              )}

              {isClosed && shouldShowTips && (

                <div className="flex justify-between text-slate-700">

                  <span className="text-slate-500">Tips</span>

                  <span className="font-medium">{formatMoney(tipsTotal)}</span>

                </div>

              )}

              {isClosed && (

                <div className="flex justify-between text-xl font-semibold text-slate-900 pt-2">

                  <span>Total</span>

                  <span>{formatMoney(totalWithTips)}</span>

                </div>

              )}

              {isClosed &&

                (paymentMethodRows.length > 0 || changeReturned > 0) && (

                  <div className="mt-3 space-y-1 text-sm text-slate-600">

                    <div className="text-base font-semibold text-slate-500">

                      Payments

                    </div>

                    <div className="flex justify-between text-slate-700">

                      <span className="text-slate-500">Change returned</span>

                      <span className="font-medium">

                        {formatMoney(changeReturned)}

                      </span>

                    </div>

                    {paymentMethodRows.map((row) => (

                      <div key={row.method} className="flex justify-between">

                        <span>{row.label}</span>

                        <span>{formatMoney(row.amount)}</span>

                      </div>

                    ))}

                  </div>

                )}

              {!isClosed && (
                <div className="flex justify-between text-xl font-semibold text-slate-900 pt-2">
                  <span>Total</span>
                  <span>{formatMoney(totalWithTips)}</span>
                </div>
              )}
            </div>
          </section>
          {isRefundEligible && (
            <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Refunds
                </div>
                <div className="text-xs text-slate-500">
                  Remaining {formatMoney(remainingRefundable)}
                </div>
              </div>
              <div className="flex justify-between text-sm text-slate-700">
                <span className="text-slate-500">Total refunded</span>
                <span className="font-medium">{formatMoney(totalRefunded)}</span>
              </div>
              {refundsError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {refundsError}
                </div>
              )}
              {refundsLoading && (
                <div className="text-sm text-slate-500">Loading refunds...</div>
              )}
              {!refundsLoading && refunds.length === 0 && (
                <div className="text-sm text-slate-500">No refunds yet.</div>
              )}
              {!refundsLoading && refunds.length > 0 && (
                <div className="space-y-2">
                  {refunds.map((refund) => (
                    <div
                      key={refund.id}
                      className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700"
                    >
                      <div>
                        <div className="font-semibold text-slate-900">
                          {formatMoney(refund.total_refund_amount)}
                        </div>
                        <div className="text-xs text-slate-500">
                          {formatDateTime(refund.created_at, ticketTimezone)} •{" "}
                          {refund.reason}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate(`/tickets/${refund.refund_ticket_id}`)}
                        className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:border-slate-300"
                      >
                        View
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
      </div>

      </SBINPageCard>

    </div>

  </div>

  );

}



















